import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { deviceDisplayName } from '../utils/deviceName';
import { DeviceIconDisplay } from '../components/icons/DeviceIcons';
import { formatDistance, formatDT, formatTimeAgo, formatDuration, getFileTimestamp } from '../utils/timezone';
import { downloadCsv } from '../utils/csv';
import { OCCUPANCY_BADGE_CLS } from '../utils/occupancy';
import { OccupancyPill } from '../components/fleet/OccupancyPill';
import { AssetLogbookDialog } from '../components/fleet/AssetLogbookDialog';
import { AssetHitchLogDialog } from '../components/fleet/AssetHitchLogDialog';
import { BulkReadingsDialog } from '../components/fleet/BulkReadingsDialog';
import { PhotoCropDialog } from '../components/fleet/PhotoCropDialog';
import { FuelPage } from '../components/fleet/FuelPage';
import { VehicleAttachments, AttachmentsViewerDialog } from '../components/shared/Attachments';
import { LogbookQuickRange } from '../components/fleet/LogbookQuickRange';
import { assetTypes, beaconAssetTypes, assetTypeLabel } from '../components/live-tracking/EditDeviceDialog';
import { MaintenanceTab } from '../components/fleet/MaintenanceTab';
import { PaginationFooter, usePersistedPageSize } from '../components/shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useSetUrlParams, useDebouncedValue, useSkipFirstEffect } from '../components/shared/useUrlPagination';
import { AssetBeaconHealthCompact } from '../components/fleet/AssetBeaconHealth';
import { exportTripCsv, exportTripPdf, exportAllTripsCsv, exportAllTripsPdf, buildAllTripsPdf, fmtCoords, tripLocMapsUrl, roleTransitionLabel } from '../utils/tripExport';
import { logReportExport } from '../utils/auditExport';
import { EmailReportModal } from '../components/EmailReportModal';
import DriverConfirmDialog from '../components/events/DriverConfirmDialog';
import ActiveDriverCorrectionDialog from '../components/events/ActiveDriverCorrectionDialog';
import { VehicleQuickView } from '../components/fleet/VehicleQuickView';
import { BulkDriverAssignmentDialog } from '../components/fleet/BulkDriverAssignmentDialog';
import { AssetQuickView } from '../components/fleet/AssetQuickView';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Truck,
  Car,
  Package,
  Wrench,
  Fuel as FuelIcon,
  Pencil,
  Search,
  Loader2,
  Shield,
  ShieldCheck,
  ClipboardList,
  Hammer,
  Box,
  Lock,
  Bluetooth,
  CreditCard,
  BookOpen,
  BookUser,
  Link2,
  History as HistoryIcon,
  UserX,
  Container,
  Users,
  IdCard,
  UserRound,
  RefreshCw,
  Route,
  Clock,
  Ban,
  Upload,
  Download,
  Mail,
  X,
  MapPin,
  Calendar as CalendarIcon,
  AlertTriangle,
  Gauge,
  MoreVertical,
  LayoutGrid,
  List as ListIcon,
  Satellite,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  SlidersHorizontal,
  Paperclip,
  Key,
  UserCog,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { useIsMobileOrWebview } from '../hooks/useIsMobileOrWebview';

// Clickable paperclip documents chip — IDENTICAL look to the People page chip.
// Red/amber border+text when a document is expired / expiring ≤30d. Opens the Documents viewer.
const VehicleDocsChip = ({ device, summary, large, onClick }) => {
  const s = summary?.[device.id];
  if (!s?.count) {
    return (
      <span
        title="No documents"
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-medium opacity-40 cursor-default text-muted-foreground border-slate-300 dark:border-slate-600 ${large ? 'min-h-[44px] px-3.5 text-sm' : 'px-1.5 py-0.5 text-xs'}`}
        data-testid={`vehicle-documents-chip-${device.id}`}
      >
        <Paperclip className={large ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
        0
      </span>
    );
  }
  const alert = s.worst === 'expired'
    ? 'border-red-300 text-red-600 dark:border-red-800 dark:text-red-400'
    : s.worst === 'expiring'
      ? 'border-amber-300 text-amber-600 dark:border-amber-800 dark:text-amber-400'
      : '';
  const title = s.worst === 'expired' ? 'Documents — a document has expired'
    : s.worst === 'expiring' ? 'Documents — a document expires within 30 days'
    : 'Documents';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-medium transition-colors hover:bg-accent ${large ? 'min-h-[44px] px-3.5 text-sm' : 'px-1.5 py-0.5 text-xs'} ${alert || 'text-muted-foreground border-slate-300 dark:border-slate-600'}`}
      data-testid={`vehicle-documents-chip-${device.id}`}
    >
      <Paperclip className={large ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
      {s.count}
    </button>
  );
};

// Vehicle 7-day Driving Behaviour score badge (Fleet list) — same bands/gating as People.
const VEH_SCORE_CLS = (score) => (score >= 85 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  : score >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400');

const VehicleScoreBadge = ({ b, deviceId, testId }) => {
  if (!b) {
    return (
      <Badge variant="outline" className="text-[10px] shrink-0 opacity-40 text-muted-foreground border-slate-300 dark:border-slate-600"
        title="No driving score — no rated driving in the last 7 days"
        data-testid={testId || `vehicle-score-badge-${deviceId}`}>
        Score —
      </Badge>
    );
  }
  if (b.rating === 'not_rated') {
    return (
      <Badge variant="outline" className="text-[10px] shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400"
        title={`Not yet rated — ${b.km} km driven in 7 days (below the minimum rated distance set in Settings)`}
        data-testid={testId || `vehicle-score-badge-${deviceId}`}>
        Not rated
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={`text-[10px] font-bold shrink-0 ${b.rating === 'low_confidence' ? 'border-dashed ' : ''}${VEH_SCORE_CLS(b.score)}`}
      title={`Driving Behaviour score (7 days): ${b.total} harsh event${b.total === 1 ? '' : 's'} over ${b.km} km${b.rating === 'low_confidence' ? ' — low confidence (limited distance driven)' : ''}`}
      data-testid={testId || `vehicle-score-badge-${deviceId}`}>
      {b.score}
    </Badge>
  );
};

// Service due/overdue chip — same pill style as the documents chip above.
const VehicleServiceChip = ({ device, status, large, onClick }) => {
  if (!status) {
    return (
      <span
        title="No service due"
        className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-medium opacity-40 cursor-default text-muted-foreground border-slate-300 dark:border-slate-600 ${large ? 'min-h-[44px] px-3.5 text-sm' : 'px-1.5 py-0.5 text-xs'}`}
        data-testid={`vehicle-service-badge-${device.id}`}
      >
        <Wrench className={large ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
      </span>
    );
  }
  const alert = status === 'overdue'
    ? 'border-red-300 text-red-600 dark:border-red-800 dark:text-red-400'
    : 'border-amber-300 text-amber-600 dark:border-amber-800 dark:text-amber-400';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={status === 'overdue' ? 'Service overdue — view schedule' : 'Service due soon — view schedule'}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-medium transition-colors hover:bg-accent ${large ? 'min-h-[44px] px-3.5 text-sm' : 'px-1.5 py-0.5 text-xs'} ${alert}`}
      data-testid={`vehicle-service-badge-${device.id}`}
    >
      <Wrench className={large ? 'w-5 h-5' : 'w-3.5 h-3.5'} />
    </button>
  );
};

// Authenticated image thumbnail (vehicle photo) — streams the blob via the API.
const AuthPhotoThumb = ({ api, photo, className = 'w-16 h-16 rounded-md object-cover border', testId = 'vehicle-photo-thumb', fallback = null }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!photo?.url) { setUrl(null); return; }
    let cancelled = false; let obj = null;
    setFailed(false);
    api.get(photo.url.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [api, photo?.url]);
  // Shared device whose photo belongs to ANOTHER account (403) → show the icon instead.
  if (failed) return fallback;
  if (!url) return <div className={`${className} bg-muted animate-pulse border-transparent`} />;
  return <img src={url} alt="Vehicle" className={className} data-testid={testId} />;
};

// Shared "⋮" overflow actions menu for fleet assets — same pattern as People /
// Geofencing rows (52px touch targets + larger trigger on mobile/WebView).
const AssetActionsMenu = ({ kind, device, mob, onAssetLogbook, onDriverLogbook, onHitchLog, onEdit }) => {
  const itemCls = mob ? 'min-h-[52px] text-sm px-3 py-3' : '';
  const iconCls = mob ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className={mob ? 'h-9 w-9 p-0' : ''} title="Actions" data-testid={`${kind}-actions-menu-${device.id}`}>
          <MoreVertical className={mob ? '!w-5 !h-5' : 'w-3.5 h-3.5'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={mob ? 'w-64 p-1.5' : 'w-52'}>
        {device.asset_editing_enabled !== false ? (
          <DropdownMenuItem className={itemCls} onSelect={onEdit} data-testid={`edit-${kind}-${device.id}`}>
            <Pencil className={iconCls} /> Edit
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem className={itemCls} disabled data-testid={`edit-${kind}-disabled-${device.id}`}>
            <Lock className={iconCls} /> Editing disabled
          </DropdownMenuItem>
        )}
        {onDriverLogbook && (
          <DropdownMenuItem className={itemCls} onSelect={onDriverLogbook} data-testid={`vehicle-driver-logbook-button-${device.id}`}>
            <BookUser className={iconCls} /> Driver Log Book
          </DropdownMenuItem>
        )}
        {onAssetLogbook && (
          <DropdownMenuItem className={itemCls} onSelect={onAssetLogbook} data-testid={`vehicle-asset-logbook-button-${device.id}`}>
            <ClipboardList className={iconCls} /> Asset Log Book
          </DropdownMenuItem>
        )}
        {onHitchLog && (
          <DropdownMenuItem className={itemCls} onSelect={onHitchLog} data-testid={`${kind}-hitch-log-button-${device.id}`}>
            {kind === 'trailer' ? <Link2 className={iconCls} /> : <Paperclip className={iconCls} />} {kind === 'trailer' ? 'Trailer Hitch Log' : 'Attachment Log'}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Phase 3B — binding filter options for the Vehicles tab.
const BIND_FILTERS = [
  { value: 'all', label: 'All vehicles', icon: Car },
  { value: 'trailer', label: 'Towing a trailer', icon: Truck },
  { value: 'equipment', label: 'Carrying equipment', icon: Container },
  { value: 'passenger', label: 'Has passengers', icon: Users },
  { value: 'staff', label: 'Has staff', icon: UserRound },
  { value: 'bound', label: 'Any binding', icon: Bluetooth },
  { value: 'none', label: 'No bindings', icon: UserX },
];

// Status colors for devices
const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
  maintenance: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400',
  offline: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
};

const fuelTypes = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'lpg', label: 'LPG' },
  { value: 'na', label: 'N/A' },
];

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <Icon className="w-12 h-12 text-muted-foreground mb-3 opacity-40" />
    <p className="text-muted-foreground font-medium">{title}</p>
    <p className="text-sm text-muted-foreground mt-1">{description}</p>
  </div>
);

// Log Book opens on the last 30 days by default (fast first paint). History is NEVER
// trimmed — the From/To pickers and quick-range presets reach the full retention window.
const defaultDriverLogbookStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); d.setHours(0, 0, 0, 0); return d; };

const buildDriverLogbookQuery = (filters, exportType = '') => {
  const params = new URLSearchParams();
  params.set('limit', String(filters.limit || 200));
  if (filters.start_time) params.set('start_time', new Date(filters.start_time).toISOString());
  if (filters.end_time) params.set('end_time', new Date(filters.end_time).toISOString());
  if (filters.source && filters.source !== 'all') params.set('source', filters.source);
  if (filters.event_type && filters.event_type !== 'all') params.set('event_type', filters.event_type);
  if (filters.person_type && filters.person_type !== 'all') params.set('person_type', filters.person_type);
  if (filters.search) params.set('search', filters.search);
  if (exportType) params.set('export', exportType);
  return params.toString();
};

export const Fleet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isSubpageEnabled, isNotificationChannelActive, isFeatureEnabled } = useFeatureFlags();

  const hasVehicles = isSubpageEnabled('fleet_vehicles');
  const hasTrailers = isSubpageEnabled('fleet_trailers');
  const hasMaintenance = isSubpageEnabled('fleet_maintenance');
  const hasFuel = isSubpageEnabled('fleet_fuel');
  const hasEquipment = isSubpageEnabled('fleet_equipment');
  const hasGreenDriving = isSubpageEnabled('fleet_green_driving');
  // Driver Assignment controls need People->Drivers or People->Staff privileges
  const hasDriverAssignPriv = isSubpageEnabled('people_drivers') || isSubpageEnabled('people_staff');
  // Travel History deep link — only when the user can actually open Analytics > History
  const hasTravelHistory = isFeatureEnabled('analytics_page') && isSubpageEnabled('analytics_history');
  const openTravelHistory = (device) => navigate(`/analytics?tab=history&device=${device.id}`);

  const getDefaultTab = useCallback(() => {
    if (hasVehicles) return 'vehicles';
    if (hasTrailers) return 'trailers';
    if (hasEquipment) return 'equipment';
    if (hasMaintenance) return 'maintenance';
    if (hasFuel) return 'fuel';
    return null;
  }, [hasVehicles, hasTrailers, hasEquipment, hasMaintenance, hasFuel]);

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = useCallback(() => {
    const allowedTabs = { vehicles: hasVehicles, trailers: hasTrailers, equipment: hasEquipment, maintenance: hasMaintenance, fuel: hasFuel };
    return allowedTabs[requestedTab] ? requestedTab : null;
  }, [hasVehicles, hasTrailers, hasEquipment, hasMaintenance, hasFuel, requestedTab]);

  // Session memory: remember the last sub-tab across navigation & F5 (same sessionStorage-backed
  // approach as Analytics/Media). Only honoured when the remembered tab is still feature-allowed.
  const getPersistedTab = useCallback(() => {
    try {
      const t = sessionStorage.getItem('fleet_subtab');
      const allowedTabs = { vehicles: hasVehicles, trailers: hasTrailers, equipment: hasEquipment, maintenance: hasMaintenance, fuel: hasFuel };
      return t && allowedTabs[t] ? t : null;
    } catch { return null; }
  }, [hasVehicles, hasTrailers, hasEquipment, hasMaintenance, hasFuel]);

  const [activeTab, setActiveTab] = useState(() => getRequestedTab() || getPersistedTab() || getDefaultTab());

  // Persist the active sub-tab whenever it changes (covers changeTab + feature-flag reconciliation).
  useEffect(() => {
    if (activeTab) { try { sessionStorage.setItem('fleet_subtab', activeTab); } catch { /* ignore */ } }
  }, [activeTab]);

  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = { vehicles: hasVehicles, trailers: hasTrailers, equipment: hasEquipment, maintenance: hasMaintenance, fuel: hasFuel };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) setActiveTab(defaultTab);
    }
  }, [activeTab, getDefaultTab, getRequestedTab, hasVehicles, hasTrailers, hasMaintenance, hasFuel, hasEquipment]);

  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // Per-subpage search (mirrors People's searchByTab fix) — each Fleet tab keeps its own query.
  const [searchByTab, setSearchByTab] = useState(() => {
    try {
      const f = JSON.parse(sessionStorage.getItem('fleet_filters') || '{}');
      if (f.searchByTab) return f.searchByTab;
      return f.search ? { vehicles: f.search } : {};
    } catch { return {}; }
  });
  const searchQuery = searchByTab[activeTab] || '';
  const setSearchQuery = (v) => setSearchByTab((prev) => ({
    ...prev,
    [activeTab]: typeof v === 'function' ? v(prev[activeTab] || '') : v,
  }));
  // ONE URL-backed page shared by whichever list tab is active (?tab=trailers&page=2).
  // Tab switches reset it via changeTab below.
  const [fleetPage, setFleetPage] = useUrlPage('page');
  const vehiclesPage = fleetPage; const setVehiclesPage = setFleetPage;
  const trailersPage = fleetPage; const setTrailersPage = setFleetPage;
  const equipmentPage = fleetPage; const setEquipmentPage = setFleetPage;
  const [fleetPageSize, setFleetPageSize] = useUrlPageSize('fleet', 25);
  const [fleetTotals, setFleetTotals] = useState({ vehicles: 0, trailers: 0, equipment: 0 });
  const [fleetCounts, setFleetCounts] = useState({ vehicles: 0, trailers: 0, equipment: 0 });
  const [serverBindingSummary, setServerBindingSummary] = useState({ trailer: 0, equipment: 0, passenger: 0, staff: 0, bound: 0 });
  const [driverCorrectionDevice, setDriverCorrectionDevice] = useState(null);
  const [bindFilter, setBindFilter] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fleet_filters') || '{}').bindFilter || 'all'; } catch { return 'all'; }
  });
  // Unified smart filters + card-grid sort (vehicles tab, server-side).
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fleet_filters') || '{}').statusFilter || 'all'; } catch { return 'all'; }
  });
  const [serviceFilter, setServiceFilter] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fleet_filters') || '{}').serviceFilter || 'all'; } catch { return 'all'; }
  });
  const [fleetSort, setFleetSort] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fleet_filters') || '{}').sort || 'name'; } catch { return 'name'; }
  });
  const [docsFilter, setDocsFilter] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fleet_filters') || '{}').docsFilter || 'all'; } catch { return 'all'; }
  });
  const activeFilterCount = [bindFilter, statusFilter, serviceFilter, docsFilter].filter((v) => v !== 'all').length;
  // Persist Fleet list filters for the session (restored on return — like Analytics/Media).
  useEffect(() => {
    try { sessionStorage.setItem('fleet_filters', JSON.stringify({ searchByTab, bindFilter, statusFilter, serviceFilter, docsFilter, sort: fleetSort })); } catch { /* ignore */ }
  }, [searchByTab, bindFilter, statusFilter, serviceFilter, docsFilter, fleetSort]);
  // Debounce the whole per-tab map (like People) so tab switches use the right
  // tab's search immediately instead of a stale 400ms-old value.
  const debouncedSearchByTab = useDebouncedValue(searchByTab, 400);
  const debouncedFleetSearch = debouncedSearchByTab[activeTab] || '';
  const setUrlParams = useSetUrlParams();
  const changeTab = (tab) => {
    setActiveTab(tab);
    setUrlParams({ tab, page: null, svc_device: null });
  };

  // Reset pagination when search / filter / page-size changes (skips mount so ?page= deep-links survive)
  useSkipFirstEffect(() => { setFleetPage(1); }, [debouncedFleetSearch, bindFilter, statusFilter, serviceFilter, docsFilter, fleetSort, fleetPageSize]);

  // Allocated devices state
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  // Row / card selection (used by the toolbar Export buttons — mirrors the
  // People-page selection pattern). Cleared automatically when the current
  // page or filter changes so users don't accidentally export stale IDs.
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectedTrailerIds, setSelectedTrailerIds] = useState([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [selectAllAcrossLoading, setSelectAllAcrossLoading] = useState(false);

  // Cross-page "Select All" — fetches every asset id matching the current
  // search/filter across ALL pages so the selection isn't limited to what's
  // currently rendered. Mirrors the DriverCorrections / Event Log pattern.
  const fetchAllFleetIds = useCallback(async (assetTab, total) => {
    const params = new URLSearchParams();
    params.set('asset_type', assetTab);
    if (debouncedFleetSearch.trim()) params.set('search', debouncedFleetSearch.trim());
    if (assetTab === 'vehicles') {
      if (bindFilter !== 'all') params.set('bind_filter', bindFilter);
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      if (serviceFilter !== 'all') params.set('service_filter', serviceFilter);
      if (docsFilter !== 'all') params.set('docs_filter', docsFilter);
    }
    params.set('skip', '0');
    params.set('limit', String(Math.max(total || 1, 1)));
    const res = await api.get(`/fleet/my-devices?${params.toString()}`);
    return (res.data?.[assetTab] || []).map(d => d.id).filter(Boolean);
  }, [api, debouncedFleetSearch, bindFilter, statusFilter, serviceFilter, docsFilter]);

  // Asset edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [bulkReadingsOpen, setBulkReadingsOpen] = useState(false);
  const [bulkReadingsCategory, setBulkReadingsCategory] = useState('vehicle');
  const [editingDevice, setEditingDevice] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [assetForm, setAssetForm] = useState({});
  const [homeVehicleOptions, setHomeVehicleOptions] = useState([]);
  const [driverAssignOptions, setDriverAssignOptions] = useState([]);
  const [driverAssignPickerOpen, setDriverAssignPickerOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [suggestingHome, setSuggestingHome] = useState(false);
  const [driverLogbookModal, setDriverLogbookModal] = useState({ open: false, loading: false, data: null, error: null });
  const [assetLogbook, setAssetLogbook] = useState({ open: false, device: null });
  const [hitchLog, setHitchLog] = useState({ open: false, device: null });
  const [driverLogbookTarget, setDriverLogbookTarget] = useState(null);
  const mob = useIsMobileOrWebview();
  // Card ⇄ compact list toggle for the Vehicles tab (desktop only, remembered per user).
  const [vehiclesView, setVehiclesView] = useState(() => {
    try { return localStorage.getItem('fleet_vehicles_view') || 'cards'; } catch { return 'cards'; }
  });
  const changeVehiclesView = (v) => {
    setVehiclesView(v);
    try { localStorage.setItem('fleet_vehicles_view', v); } catch {}
  };
  // Card ⇄ list toggle for the Trailers / Equipment tabs (desktop, cards default).
  const [assetsViewByTab, setAssetsViewByTab] = useState(() => {
    try {
      return {
        trailers: localStorage.getItem('fleet_trailers_view') || 'cards',
        equipment: localStorage.getItem('fleet_equipment_view') || 'cards',
      };
    } catch { return { trailers: 'cards', equipment: 'cards' }; }
  });
  const changeAssetsView = (tab, v) => {
    setAssetsViewByTab((prev) => ({ ...prev, [tab]: v }));
    try { localStorage.setItem(`fleet_${tab}_view`, v); } catch {}
  };
  // List view column sorting (client-side on the loaded page).
  const [vehiclesSort, setVehiclesSort] = useState({ key: null, dir: 'asc' });
  const toggleVehiclesSort = (key) => {
    setVehiclesSort((prev) => prev.key === key
      ? (prev.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' })
      : { key, dir: 'asc' });
  };
  // Quick View sheet — opened by tapping a vehicle card.
  const [quickViewDevice, setQuickViewDevice] = useState(null);

  // Consume-once: ?qv=<deviceId> (from the Person Dashboard "Driving …" pill) opens the Vehicle Dashboard.
  const requestedQv = new URLSearchParams(location.search).get('qv') || '';
  const qvFetchRef = useRef(null);
  useEffect(() => {
    if (!requestedQv) return;
    const consumeQv = () => {
      const p = new URLSearchParams(location.search);
      p.delete('qv');
      navigate({ search: p.toString() }, { replace: true });
    };
    const dev = [...vehicles, ...trailers, ...equipment].find((d) => d.id === requestedQv);
    if (dev) {
      setQuickViewDevice(dev);
      consumeQv();
      return;
    }
    // Not on the current server-side page — fetch the device directly so the
    // deep-link works regardless of pagination.
    if (!hasLoadedOnce || qvFetchRef.current === requestedQv) return;
    qvFetchRef.current = requestedQv;
    api.get(`/fleet/my-devices?device_id=${encodeURIComponent(requestedQv)}`)
      .then((r) => {
        const d = [...(r.data?.vehicles || []), ...(r.data?.trailers || []), ...(r.data?.equipment || [])]
          .find((x) => x.id === requestedQv);
        if (d) setQuickViewDevice(d);
      })
      .catch(() => {})
      .finally(consumeQv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedQv, vehicles, trailers, equipment, hasLoadedOnce]);
  const [photoBusy, setPhotoBusy] = useState(false);
  // Live Snapshot Sync: the fleet lists silently refresh every 60s — push each fresh
  // device object into the open dashboard so its fallback prop is never stale.
  useEffect(() => {
    if (!quickViewDevice) return;
    const fresh = [...vehicles, ...trailers, ...equipment].find((d) => d.id === quickViewDevice.id);
    if (fresh && fresh !== quickViewDevice) setQuickViewDevice(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, trailers, equipment]);
  const [vehicleDocs, setVehicleDocs] = useState([]);
  // Per-vehicle document chip data (count + worst expiry) — one batch call.
  const [docsSummary, setDocsSummary] = useState({});
  const refreshDocsSummary = React.useCallback(() => {
    api.get('/fleet/vehicle-documents/summary')
      .then((r) => setDocsSummary(r.data?.summary || {}))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { refreshDocsSummary(); }, [refreshDocsSummary]);
  // Documents viewer modal (same UX as the People page chip): fetch + show read-only list.
  const [docsViewer, setDocsViewer] = useState(null); // { device, attachments }
  const openDocsViewer = (device) => {
    api.get(`/fleet/vehicles/${device.id}/attachments`)
      .then((r) => setDocsViewer({ device, attachments: r.data?.attachments || [] }))
      .catch(() => setDocsViewer({ device, attachments: [] }));
  };
  useEffect(() => {
    if (!editingDevice?.id) { setVehicleDocs([]); return; }
    let cancelled = false;
    api.get(`/fleet/vehicles/${editingDevice.id}/attachments`)
      .then((r) => { if (!cancelled) setVehicleDocs(r.data?.attachments || []); })
      .catch(() => { if (!cancelled) setVehicleDocs([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDevice?.id]);
  const [cropSrc, setCropSrc] = useState(null);

  // File select opens the crop/reposition dialog instead of uploading directly.
  const handleVehiclePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editingDevice) return;
    setCropSrc(URL.createObjectURL(file));
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleCroppedPhoto = async (blob) => {
    if (!editingDevice) return;
    const fd = new FormData();
    fd.append('file', new File([blob], 'vehicle-photo.jpg', { type: 'image/jpeg' }));
    setPhotoBusy(true);
    try {
      const r = await api.post(`/fleet/vehicles/${editingDevice.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditingDevice((p) => ({ ...p, vehicle_photo: r.data.photo }));
      toast.success('Vehicle photo updated');
      fetchFleetDevices();
      closeCrop();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleVehiclePhotoRemove = async () => {
    if (!editingDevice?.vehicle_photo) return;
    setPhotoBusy(true);
    try {
      await api.delete(`/fleet/vehicles/${editingDevice.id}/photo`);
      setEditingDevice((p) => ({ ...p, vehicle_photo: null }));
      toast.success('Vehicle photo removed');
      fetchFleetDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo removal failed');
    } finally {
      setPhotoBusy(false);
    }
  };
  const [driverLogbookFilters, setDriverLogbookFilters] = useState({ start_time: defaultDriverLogbookStart(), end_time: null, source: 'all', event_type: 'all', person_type: 'all', search: '', limit: 200 });
  const [selectedTrips, setSelectedTrips] = useState(() => new Set());
  const [lbStartOpen, setLbStartOpen] = useState(false);
  const [lbEndOpen, setLbEndOpen] = useState(false);
  const [tripPage, setTripPage] = useState(0);
  const [tripsTotal, setTripsTotal] = useState(0);
  const suppressTripPageFetchRef = useRef(false);
  const driverLogbookTripsRef = useRef(null);
  const driverLogbookReturnRef = useRef(null);
  const [tripsPageSize, setTripsPageSize] = usePersistedPageSize('vehicle-driver-logbook', 10);
  const [driverConfirm, setDriverConfirm] = useState({ open: false, candidateId: null });

  const [assetHealth, setAssetHealth] = useState({});
  const [svcStatusByDevice, setSvcStatusByDevice] = useState({});
  // Per-vehicle 7d behaviour scores (Score column) — fetched once per visit (server caches 10 min).
  const [behaviourByDevice, setBehaviourByDevice] = useState(null);
  useEffect(() => {
    if (!hasGreenDriving || activeTab !== 'vehicles' || behaviourByDevice !== null) return;
    api.get('/fleet/behaviour-summary?days=7')
      .then((r) => setBehaviourByDevice(r.data?.vehicles || {}))
      .catch(() => setBehaviourByDevice({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, behaviourByDevice, hasGreenDriving]);

  const fetchFleetDevices = useCallback(async (silent) => {
    const isSilent = silent === true;
    if (!isSilent) setLoading(true);
    try {
      // Server-side pagination: fetch only the active list tab's filtered page.
      const listTab = ['vehicles', 'trailers', 'equipment'].includes(activeTab) ? activeTab : 'vehicles';
      const params = new URLSearchParams();
      params.set('asset_type', listTab);
      if (debouncedFleetSearch.trim()) params.set('search', debouncedFleetSearch.trim());
      if (listTab === 'vehicles') {
        if (bindFilter !== 'all') params.set('bind_filter', bindFilter);
        if (statusFilter !== 'all') params.set('status_filter', statusFilter);
        if (serviceFilter !== 'all') params.set('service_filter', serviceFilter);
        if (docsFilter !== 'all') params.set('docs_filter', docsFilter);
        if (fleetSort !== 'name') params.set('sort', fleetSort);
      }
      params.set('skip', String((fleetPage - 1) * fleetPageSize));
      params.set('limit', String(fleetPageSize));
      const response = await api.get(`/fleet/my-devices?${params.toString()}`);
      if (listTab === 'vehicles') setVehicles(response.data.vehicles || []);
      if (listTab === 'trailers') setTrailers(response.data.trailers || []);
      if (listTab === 'equipment') setEquipment(response.data.equipment || []);
      setFleetTotals((prev) => ({ ...prev, ...(response.data.totals || {}) }));
      setFleetCounts(response.data.counts || { vehicles: 0, trailers: 0, equipment: 0 });
      setServerBindingSummary(response.data.binding_summary || { trailer: 0, equipment: 0, passenger: 0, staff: 0, bound: 0 });
    } catch (error) {
      console.error('Failed to fetch fleet devices:', error);
    } finally {
      if (!isSilent) setLoading(false);
      setHasLoadedOnce(true);
    }    try {
      const health = await api.get('/fleet/assets/beacon-health');
      const map = {};
      (health.data.assets || []).forEach((a) => { map[a.beacon_device_id] = a; });
      setAssetHealth(map);
    } catch (error) {
      console.error('Failed to fetch asset beacon health:', error);
    }
    try {
      if (hasMaintenance) {
        const svc = await api.get('/service/summary');
        setSvcStatusByDevice(svc.data?.device_status || {});
      }
    } catch { /* non-blocking */ }
  }, [api, activeTab, debouncedFleetSearch, bindFilter, statusFilter, serviceFilter, docsFilter, fleetSort, fleetPage, fleetPageSize, hasMaintenance]);

  useEffect(() => {
    fetchFleetDevices();
  }, [fetchFleetDevices]);

  // Silent 60s auto-refresh on the three fleet list tabs (same cadence as VehicleQuickView).
  useEffect(() => {
    if (!['vehicles', 'trailers', 'equipment'].includes(activeTab)) return undefined;
    const iv = setInterval(() => fetchFleetDevices(true), 60000);
    return () => clearInterval(iv);
  }, [activeTab, fetchFleetDevices]);

  // Open asset edit dialog
  const openAssetEdit = (device) => {
    setEditingDevice(device);
    setAssetForm({
      asset_name: device.asset_name || '',
      asset_type: device.asset_type || '',
      asset_make: device.asset_make || '',
      asset_model: device.asset_model || '',
      asset_year: device.asset_year || '',
      registration: device.registration || '',
      vin_number: device.vin_number || '',
      fuel_type: device.fuel_type || '',
      serial_no: device.serial_no || '',
      container_number: device.container_number || '',
      container_size: device.container_size || '',
      container_type: device.container_type || '',
      asset_description: device.asset_description || '',
      odometer_reading: device.computed_odometer ?? device.odometer_reading ?? '',
      engine_hours: device.computed_engine_hours ?? device.engine_hours ?? '',
      tank_capacity: device.tank_capacity ?? '',
      battery_capacity_kwh: device.battery_capacity_kwh ?? '',
      fuel_reference_consumption: device.fuel_reference_consumption ?? '',
      fuel_price: device.fuel_price ?? '',
      home_vehicle_id: device.home_vehicle_id || '',
      driver_assignment_mode: device.driver_assignment_mode || 'auto',
      manual_assigned_person: device.manual_assigned_person
        ? `${device.manual_assigned_person.person_type}:${device.manual_assigned_person.person_id}` : '',
    });
    if (device.device_type === 'beacon') {
      api.get('/fleet/home-vehicle-options')
        .then((r) => setHomeVehicleOptions(r.data || []))
        .catch(() => setHomeVehicleOptions([]));
    } else if (isVehicleType(device.asset_type)) {
      setDriverAssignPickerOpen(false);
      api.get(`/people/options?driver_eligible_only=true&assignable_for_device=${device.id}`)
        .then((r) => setDriverAssignOptions(r.data || []))
        .catch(() => setDriverAssignOptions([]));
    }
    setIsEditDialogOpen(true);
  };

  const suggestHomeVehicle = async () => {
    if (!editingDevice) return;
    setSuggestingHome(true);
    try {
      const r = await api.get(`/fleet/devices/${editingDevice.id}/home-vehicle-suggestion`);
      const s = r.data?.suggestion;
      if (s?.vehicle_id) {
        setAssetForm(prev => ({ ...prev, home_vehicle_id: s.vehicle_id }));
        toast.success(`Suggested ${s.vehicle_name} — carried this asset ${s.times_seen} time(s) in the last 14 days`);
      } else {
        toast.info('No binding history in the last 14 days to suggest from');
      }
    } catch (e) {
      toast.error('Could not fetch a suggestion');
    } finally {
      setSuggestingHome(false);
    }
  };

  // ── Shared fleet CSV exporter — mirrors the People-page export UX. Exports
  // the ticked rows; if nothing is selected, exports the currently visible
  // rows (respecting the active search / binding filter). Column set is
  // deliberately narrow — Name, Registration, IMEI, Type, Make, Model, Year,
  // VIN, Fuel, Odometer, Notes — so the CSV opens cleanly in Excel.
  const exportFleetCsv = useCallback((items, selectedIds, kind) => {
    const ids = new Set(selectedIds || []);
    const rowsToExport = ids.size > 0 ? items.filter((d) => ids.has(d.id)) : items;
    if (!rowsToExport.length) {
      return;
    }
    const headers = ['Name', 'Registration', 'IMEI', 'Type', 'Make', 'Model', 'Year', 'VIN', 'Fuel', 'Odometer (km)', 'Notes'];
    const rows = rowsToExport.map((d) => [
      deviceDisplayName(d),
      d.registration || '',
      d.imei || '',
      d.asset_type || '',
      d.asset_make || '',
      d.asset_model || '',
      d.asset_year || '',
      d.vin_number || '',
      d.fuel_type || '',
      d.computed_odometer ?? d.odometer_reading ?? '',
      d.notes || '',
    ]);
    const ts = getFileTimestamp();
    downloadCsv(`${kind}_${ts}.csv`, headers, rows);
  }, []);

  // True server-side pagination: fetch one trips page (`skip`/`limit`), total from X-Total-Count.
  const fetchDriverLogbookPage = useCallback(async (device, filters, page, size, focusTripId) => {
    const params = new URLSearchParams(buildDriverLogbookQuery(filters));
    params.set('limit', String(size));
    params.set('skip', String(page * size));
    if (focusTripId) params.set('focus_trip_id', focusTripId);
    const response = await api.get(`/fleet/vehicles/${device.id}/driver-logbook?${params.toString()}`);
    setTripsTotal(parseInt(response.headers?.['x-total-count'], 10) || response.data?.total_trips || 0);
    return response;
  }, [api]);

  // Full matching window (up to 1000 trips) — used by exports only (they need distances).
  const fetchAllDriverLogbookTrips = useCallback(async () => {
    if (!driverLogbookTarget) return [];
    const params = new URLSearchParams(buildDriverLogbookQuery(driverLogbookFilters));
    params.set('limit', '1000');
    params.set('skip', '0');
    const response = await api.get(`/fleet/vehicles/${driverLogbookTarget.id}/driver-logbook?${params.toString()}`);
    return response.data?.trips || [];
  }, [api, driverLogbookTarget, driverLogbookFilters]);

  // Select All: ids-only fetch — no distances/entries, so it's near-instant.
  const fetchAllDriverLogbookTripIds = useCallback(async () => {
    if (!driverLogbookTarget) return [];
    const params = new URLSearchParams(buildDriverLogbookQuery(driverLogbookFilters));
    params.set('limit', '1000');
    params.set('skip', '0');
    params.set('ids_only', '1');
    const response = await api.get(`/fleet/vehicles/${driverLogbookTarget.id}/driver-logbook?${params.toString()}`);
    return response.data?.trip_ids || [];
  }, [api, driverLogbookTarget, driverLogbookFilters]);

  const openVehicleDriverLogbook = useCallback(async (device, filters, options = {}) => {
    setDriverLogbookTarget(device);
    setDriverLogbookModal({ open: true, loading: true, data: null, error: null });
    setSelectedTrips(new Set());
    try {
      let page = 0;
      let foundIndex = -1;
      let foundTrip = null;
      // Deep link: the server locates the trip and returns ITS page in this single request
      // (focus_index = absolute index; filters.skip = the page offset it landed on).
      const response = await fetchDriverLogbookPage(device, filters, 0, tripsPageSize, options.targetTripId);
      if (options.targetTripId && Number.isInteger(response.data?.focus_index)) {
        foundIndex = response.data.focus_index;
        page = Math.floor(foundIndex / tripsPageSize);
        foundTrip = (response.data?.trips || [])[foundIndex - (response.data?.filters?.skip || 0)] || null;
        // The server may have WIDENED the window to reach an older focused trip —
        // adopt its effective start so later page changes stay in the same window.
        const effStart = response.data?.filters?.start_time;
        if (effStart) setDriverLogbookFilters((p) => ({ ...p, start_time: new Date(effStart) }));
      }
      setTripPage((prev) => {
        if (prev !== page) suppressTripPageFetchRef.current = true;
        return page;
      });
      if (foundTrip) {
        setSelectedTrips(new Set([foundTrip.id]));
        window.setTimeout(() => {
          document.querySelector(`[data-testid="vehicle-driver-logbook-trip-${foundIndex}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 150);
      }
      setDriverLogbookModal({ open: true, loading: false, data: response.data, error: null });
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to load vehicle driver log book';
      setDriverLogbookModal({ open: true, loading: false, data: null, error: message });
      toast.error(message);
    }
  }, [tripsPageSize, fetchDriverLogbookPage]);

  // Refetch the trips page when the user changes page or rows-per-page.
  useEffect(() => {
    if (suppressTripPageFetchRef.current) { suppressTripPageFetchRef.current = false; return; }
    if (!driverLogbookModal.open || !driverLogbookTarget) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchDriverLogbookPage(driverLogbookTarget, driverLogbookFilters, tripPage, tripsPageSize);
        if (!cancelled) setDriverLogbookModal((prev) => (prev.open ? { ...prev, loading: false, data: response.data, error: null } : prev));
      } catch { /* keep showing the existing page */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPage, tripsPageSize]);

  useEffect(() => {
    if (loading || !vehicles.length) return;
    const params = new URLSearchParams(location.search);
    const targetDeviceId = params.get('driver_logbook_device');
    const targetTripId = params.get('trip_id');
    if (!targetDeviceId) return;
    // The vehicle list is server-side paginated, so the target vehicle may not be
    // on the loaded page. Fall back to an id-only stub: the log book endpoint does
    // its own access check and returns the vehicle details in its payload.
    const target = vehicles.find((v) => v.id === targetDeviceId) || { id: targetDeviceId };
    // Round-trip marker from a Person Log Book: closing the modal returns there.
    driverLogbookReturnRef.current = params.get('return_person_logbook') || null;
    setActiveTab('vehicles');
    openVehicleDriverLogbook(target, driverLogbookFilters, { targetTripId });
    params.delete('driver_logbook_device');
    params.delete('trip_id');
    params.delete('return_person_logbook');
    navigate(`/fleet${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
  }, [driverLogbookFilters, loading, location.search, navigate, openVehicleDriverLogbook, vehicles]);

  const applyVehicleDriverLogbookFilters = () => {
    if (!driverLogbookTarget) return;
    openVehicleDriverLogbook(driverLogbookTarget, driverLogbookFilters);
  };

  // Silent in-place refresh: keeps the current page + scroll position so the manager
  // stays focused on the trip card they just processed (no loading remount, no page reset).
  const refreshVehicleDriverLogbookInPlace = useCallback(async () => {
    if (!driverLogbookTarget || !driverLogbookModal.open) return;
    try {
      const response = await fetchDriverLogbookPage(driverLogbookTarget, driverLogbookFilters, tripPage, tripsPageSize);
      setDriverLogbookModal((prev) => (prev.open ? { ...prev, loading: false, data: response.data, error: null } : prev));
    } catch { /* keep showing the existing data */ }
  }, [driverLogbookTarget, driverLogbookModal.open, driverLogbookFilters, tripPage, tripsPageSize, fetchDriverLogbookPage]);

  const openDriverBallot = (candidateId) => {
    if (!candidateId) return;
    setDriverConfirm({ open: true, candidateId });
  };

  const toggleTripSelection = (id) => setSelectedTrips((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const logTripExport = (fmt, count) => logReportExport(api, {
    report: 'Vehicle Driver Log Book',
    format: fmt,
    scope: driverLogbookModal.data?.vehicle?.name || 'vehicle',
    count,
    entity_id: driverLogbookModal.data?.vehicle?.id,
  });

  const exportAllTrips = async (fmt) => {
    const vname = driverLogbookModal.data?.vehicle?.name;
    try {
      const all = await fetchAllDriverLogbookTrips();
      const trips = selectedTrips.size ? all.filter((t) => selectedTrips.has(t.id)) : all;
      if (!trips.length) { toast.info('No trips to export'); return; }
      logTripExport(fmt === 'pdf' ? 'pdf' : 'csv', trips.length);
      if (fmt === 'pdf') {
        exportAllTripsPdf(trips, vname).catch(() => toast.error('Could not generate PDF'));
      } else {
        exportAllTripsCsv(trips, vname);
      }
    } catch { toast.error('Could not load trips for export'); }
  };

  const [emailModal, setEmailModal] = useState({ open: false, ctx: null });
  const openEmailTrips = () => {
    const vname = driverLogbookModal.data?.vehicle?.name;
    const count = selectedTrips.size || tripsTotal;
    if (!count) { toast.info('No trips to email'); return; }
    setEmailModal({
      open: true,
      ctx: {
        reportLabel: 'Vehicle Driver Log Book',
        scope: vname || 'vehicle',
        count,
        subject: `EnsueLIVETRAK - Vehicle Driver Log Book${vname ? ' - ' + vname : ''}`,
        buildPdf: async () => {
          const all = await fetchAllDriverLogbookTrips();
          const trips = selectedTrips.size ? all.filter((t) => selectedTrips.has(t.id)) : all;
          return buildAllTripsPdf(trips, vname);
        },
      },
    });
  };

  const handleSaveAsset = async () => {
    if (!editingDevice) return;
    setEditSubmitting(true);
    try {
      const payload = {};
      // Only send odometer/engine hours when the user actually changed them —
      // prevents resetting the server-side GPS/ignition accumulation counters.
      const loadedOdo = editingDevice.computed_odometer ?? editingDevice.odometer_reading ?? 0;
      const loadedEh = editingDevice.computed_engine_hours ?? editingDevice.engine_hours ?? 0;
      for (const [key, value] of Object.entries(assetForm)) {
        if (key === 'home_vehicle_id' || key === 'driver_assignment_mode' || key === 'manual_assigned_person') continue; // handled explicitly below
        if (value !== '' && value !== null && value !== undefined) {
          if (key === 'odometer_reading') {
            const v = parseFloat(value) || 0;
            if (editingDevice.odometer_source !== 'sensor' && v !== loadedOdo) payload[key] = v;
          } else if (key === 'engine_hours') {
            const v = parseFloat(value) || 0;
            if (editingDevice.engine_hours_source !== 'sensor' && v !== loadedEh) payload[key] = v;
          } else if (['tank_capacity', 'battery_capacity_kwh', 'fuel_reference_consumption', 'fuel_price'].includes(key)) {
            const v = parseFloat(value);
            if (!Number.isNaN(v)) payload[key] = v;
          } else {
            payload[key] = value;
          }
        }
      }
      if (editingDevice.device_type === 'beacon'
          && (assetForm.home_vehicle_id || '') !== (editingDevice.home_vehicle_id || '')) {
        payload.home_vehicle_id = assetForm.home_vehicle_id || null;
      }
      // Manual Driver Allocation (vehicles only) — send only when changed.
      if (editingDevice.device_type !== 'beacon' && isVehicleType(assetForm.asset_type || editingDevice.asset_type)) {
        const loadedMode = editingDevice.driver_assignment_mode || 'auto';
        const loadedPerson = editingDevice.manual_assigned_person
          ? `${editingDevice.manual_assigned_person.person_type}:${editingDevice.manual_assigned_person.person_id}` : '';
        const mode = assetForm.driver_assignment_mode || 'auto';
        if (mode !== loadedMode || (mode === 'manual' && (assetForm.manual_assigned_person || '') !== loadedPerson)) {
          if (mode === 'manual' && !assetForm.manual_assigned_person) {
            toast.error('Select a driver or eligible staff member for manual assignment');
            setEditSubmitting(false);
            return;
          }
          payload.driver_assignment_mode = mode;
          if (mode === 'manual') {
            const [pt, pid] = assetForm.manual_assigned_person.split(':');
            payload.manual_assigned_person_type = pt;
            payload.manual_assigned_person_id = pid;
          }
        }
      }
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setIsEditDialogOpen(false);
        setEditingDevice(null);
        return;
      }
      await api.put(`/fleet/devices/${editingDevice.id}/asset`, payload);
      toast.success('Asset details updated');
      setIsEditDialogOpen(false);
      setEditingDevice(null);
      fetchFleetDevices();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'object' && d ? `${(d.loc || []).slice(1).join('.')}: ${d.msg}` : String(d))).join('; ')
        : (typeof detail === 'object' && detail ? JSON.stringify(detail) : detail);
      if (msg === 'Asset editing is not enabled for this device') {
        toast.error('Editing is not enabled for this device. Contact your administrator.');
      } else {
        toast.error(msg || 'Failed to update asset details');
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  // Filter items by search
  // NOTE (iter551): search + bind filtering moved SERVER-SIDE into
  // GET /fleet/my-devices (asset_type/search/bind_filter/skip/limit); the old
  // client-side filterItems/matchesBindFilter helpers were removed.

  // Determine which asset fields to show in edit based on asset type
  const isVehicleType = (type) => ['car', 'suv', 'van', 'ute', 'truck', 'bus', 'motorcycle', 'scooter', 'boat', 'jetski', 'caravan', 'ebike', 'machine', 'excavator', 'vehicle'].includes(type);
  const isContainerType = (type) => type === 'container';
  const isTrailerType = (type) => type === 'trailer';
  const isAssetBeacon = (device) => device?.device_type === 'beacon' && (device?.beacon_purpose || 'asset') === 'asset';
  const peopleTabForType = (personType) => ({ driver: 'drivers', staff: 'staff', passenger: 'passengers' }[personType] || 'drivers');
  const openPersonSearch = (session) => {
    // Prefer the pagination-proof deep link that opens the Person Dashboard directly.
    if (session?.person_id) {
      navigate(`/people?open_person=${session.person_type || 'driver'}:${session.person_id}`);
      return;
    }
    if (!session?.person_name) return;
    const params = new URLSearchParams({ tab: peopleTabForType(session.person_type), search: session.person_name });
    navigate(`/people?${params.toString()}`);
  };
  const isIgnitionOn = (device) => device?.ignition_on === true || device?.ignition === true || device?.last_io_elements?.['239'] === 1 || device?.last_io_elements?.['239'] === '1';
  const currentDriverSessionFor = (device) => {
    const session = device.active_driver_session || device.current_driver_session;
    return isIgnitionOn(device) && session?.person_name ? session : null;
  };

  // Phase 3B — binding helpers
  const bindingCounts = (d) => {
    const cb = d.current_bindings || {};
    return {
      trailer: (cb.trailer || []).length,
      equipment: (cb.equipment || []).length,
      passenger: (cb.passenger || []).length,
      staff: (cb.staff || []).length,
    };
  };
  const hasAnyBinding = (d) => {
    const c = bindingCounts(d);
    return c.trailer + c.equipment + c.passenger + c.staff > 0;
  };
  const fleetBindingSummary = serverBindingSummary;
  const BINDING_BADGE_META = {
    driver: { icon: IdCard, label: 'Driver', singular: 'driver', plural: 'drivers', cls: OCCUPANCY_BADGE_CLS.driver },
    staff: { icon: UserRound, label: 'Staff', singular: 'staff', plural: 'staff', cls: OCCUPANCY_BADGE_CLS.staff },
    passenger: { icon: Users, label: 'Passengers', singular: 'passenger', plural: 'passengers', cls: OCCUPANCY_BADGE_CLS.passenger },
    trailer: { icon: Truck, label: 'Trailer', singular: 'trailer', plural: 'trailers', cls: OCCUPANCY_BADGE_CLS.trailer },
    equipment: { icon: Container, label: 'Equipment', singular: 'equipment', plural: 'equipment', cls: OCCUPANCY_BADGE_CLS.equipment },
  };

  // Full-page spinner ONLY before the first load — later refetches (search
  // debounce, pagination) keep the page mounted so inputs never lose focus.
  // Stacked card row for Trailers/Equipment on phones (Mobile Card Row standard).
  const renderAssetMobileCard = (device, kind) => {
    const selectedIds = kind === 'trailer' ? selectedTrailerIds : selectedEquipmentIds;
    const setSelected = kind === 'trailer' ? setSelectedTrailerIds : setSelectedEquipmentIds;
    return (
      <Card
        key={device.id}
        className={`cursor-pointer active:bg-muted/40 transition-colors ${selectedIds.includes(device.id) ? 'ring-2 ring-[#2563eb]' : ''}`}
        onClick={(e) => {
          if (e.target.closest('button, a, input, [role="checkbox"], [role="menuitem"], [role="dialog"]')) return;
          setQuickViewDevice(device);
        }}
        data-testid={`${kind}-mobile-card-${device.id}`}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <Checkbox
                className="h-5 w-5 shrink-0"
                checked={selectedIds.includes(device.id)}
                onCheckedChange={(checked) => setSelected((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                aria-label={`Select ${deviceDisplayName(device)}`}
                data-testid={`${kind}-select-${device.id}`}
              />
              {kind === 'trailer' ? <Package className="w-5 h-5 text-[#2563eb] shrink-0" /> : <Hammer className="w-5 h-5 text-[#2563eb] shrink-0" />}
              {isAssetBeacon(device)
                ? <Bluetooth className="w-4 h-4 text-teal-600 shrink-0" data-testid={`${kind}-beacon-icon-${device.id}`} />
                : <Satellite className="w-4 h-4 text-sky-600 shrink-0" data-testid={`${kind}-gps-icon-${device.id}`} />}
              <div className="min-w-0">
                <button type="button" className="text-sm font-semibold truncate text-left hover:text-[#2563eb] hover:underline block" onClick={() => setQuickViewDevice(device)} data-testid={`${kind}-title-${device.id}`}>{deviceDisplayName(device)}</button>
                <p className="text-xs text-muted-foreground font-mono truncate">{device.registration || device.serial_no || device.container_number || device.serial_number || device.imei}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {hasTravelHistory && (
                <Button size="sm" variant="outline" className="h-9 w-9 p-0" onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`${kind}-travel-history-button-${device.id}`}>
                  <HistoryIcon className="!w-5 !h-5" />
                </Button>
              )}
              <AssetActionsMenu kind={kind} device={device} mob onHitchLog={isAssetBeacon(device) ? () => setHitchLog({ open: true, device }) : null} onEdit={() => openAssetEdit(device)} />
            </div>
          </div>
          <div className="flex justify-between items-center gap-2 pb-2 border-b text-xs text-muted-foreground">
            <span className="truncate">Device: {device.name}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <VehicleDocsChip device={device} summary={docsSummary} large onClick={() => openDocsViewer(device)} />
              {hasMaintenance && (
                <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} large onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
              )}
              <Badge variant="outline" className={`text-xs shrink-0 ${device.is_active ? statusColors.active : statusColors.offline}`}>
                {device.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            {kind === 'equipment' && (
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">Type</span><Badge variant="outline" className="capitalize">{(device.asset_type || '').replace('_', ' ')}</Badge></div>
            )}
            {kind === 'trailer' && device.asset_make && (
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">Make/Model</span><span className="font-medium text-right">{device.asset_make} {device.asset_model}</span></div>
            )}
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Home Vehicle</span>
              {device.home_vehicle_name
                ? <span className="font-medium text-teal-600 dark:text-teal-400 text-right" data-testid={`${kind}-home-vehicle-${device.id}`}>{device.home_vehicle_name}</span>
                : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
          {isAssetBeacon(device)
            ? <AssetBeaconHealthCompact health={assetHealth[device.id]} deviceId={device.id} />
            : renderAssetLiveCell(device, kind)}
        </CardContent>
      </Card>
    );
  };

  // ── Type-aware live cell: BLE beacon → beacon health; GPS tracker → live status + last seen ──
  const ASSET_STATUS_CLS = {
    moving: 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:bg-emerald-950/40',
    idle: 'text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:bg-amber-950/40',
    parked: 'text-blue-700 border-blue-300 bg-blue-50 dark:text-blue-300 dark:border-blue-800 dark:bg-blue-950/40',
    online: 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:bg-emerald-950/40',
    offline: 'text-muted-foreground border-slate-300 dark:border-slate-600',
  };
  const assetLiveStatus = (device) => {
    const st = (device.status || 'offline').toLowerCase();
    if ((device.asset_type || '').toLowerCase() === 'building') return st === 'offline' ? 'offline' : 'online';
    return ['moving', 'idle', 'offline'].includes(st) ? st : 'parked';
  };
  const renderAssetLiveCell = (device, kind) => (isAssetBeacon(device)
    ? <AssetBeaconHealthCompact health={assetHealth[device.id]} deviceId={device.id} />
    : (
      <div className="space-y-0.5" data-testid={`${kind}-gps-status-${device.id}`}>
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${ASSET_STATUS_CLS[assetLiveStatus(device)]}`}>{assetLiveStatus(device)}</Badge>
        {(device.last_seen || device.last_gps_time) && <p className="text-[11px] text-muted-foreground">{formatTimeAgo(device.last_seen || device.last_gps_time)}</p>}
      </div>
    ));
  const assetAttachedVehicle = (device) => {
    const b = assetHealth[device.id]?.binding;
    return b?.status === 'attached' ? (b.vehicle_name || 'Vehicle') : null;
  };

  // Desktop card for Trailers/Equipment — exact Vehicles-card layout parity.
  const renderAssetDesktopCard = (device, kind) => {
    const selectedIds = kind === 'trailer' ? selectedTrailerIds : selectedEquipmentIds;
    const setSelected = kind === 'trailer' ? setSelectedTrailerIds : setSelectedEquipmentIds;
    const beacon = isAssetBeacon(device);
    const attached = assetAttachedVehicle(device);
    return (
      <Card
        key={device.id}
        className={`hover:shadow-lg dark:hover:bg-slate-800/40 transition-all cursor-pointer ${selectedIds.includes(device.id) ? 'ring-2 ring-[#2563eb]' : ''}`}
        onClick={(e) => {
          if (e.target.closest('button, a, input, [role="checkbox"], [role="menuitem"], [role="dialog"]')) return;
          setQuickViewDevice(device);
        }}
        data-testid={`${kind}-card-${device.id}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectedIds.includes(device.id)}
                onCheckedChange={(checked) => setSelected((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                aria-label={`Select ${deviceDisplayName(device)}`}
                data-testid={`${kind}-card-select-${device.id}`}
              />
              {device.vehicle_photo?.url ? (
                <AuthPhotoThumb api={api} photo={device.vehicle_photo} className="w-12 h-12 rounded-lg object-cover border" testId={`${kind}-card-photo-${device.id}`}
                  fallback={
                    <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                      <DeviceIconDisplay iconName={device.device_icon || device.asset_type || (kind === 'trailer' ? 'trailer' : 'tool')} className="w-7 h-7" iconColor={device.device_icon_color} />
                    </div>
                  } />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                  <DeviceIconDisplay iconName={device.device_icon || device.asset_type || (kind === 'trailer' ? 'trailer' : 'tool')} className="w-7 h-7" iconColor={device.device_icon_color} />
                </div>
              )}
              <div>
                <CardTitle className="text-base" data-testid={`${kind}-card-title-${device.id}`}>{deviceDisplayName(device)}</CardTitle>
                <p className="text-sm text-muted-foreground">{device.registration || device.serial_no || device.container_number || device.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {hasTravelHistory && (
                <Button size="sm" variant="outline" onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`${kind}-card-travel-history-${device.id}`}>
                  <HistoryIcon className="w-3 h-3" />
                </Button>
              )}
              <AssetActionsMenu kind={kind} device={device} mob={false} onHitchLog={beacon ? () => setHitchLog({ open: true, device }) : null} onEdit={() => openAssetEdit(device)} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {/* ── Status badge row — same placement as the Vehicles card ── */}
          <div className="pb-2 border-b flex flex-wrap items-center gap-1.5" data-testid={`${kind}-card-badges-${device.id}`}>
            <Badge variant="outline" className={`text-xs ${device.is_active ? statusColors.active : statusColors.offline}`} data-testid={`${kind}-card-active-badge-${device.id}`}>
              {device.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <VehicleDocsChip device={device} summary={docsSummary} onClick={() => openDocsViewer(device)} />
            {hasMaintenance && (
              <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
            )}
            {beacon ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-medium text-teal-700 border-teal-300 bg-teal-50 dark:text-teal-300 dark:border-teal-800 dark:bg-teal-950/40" data-testid={`${kind}-card-ble-icon-${device.id}`}>
                <Bluetooth className="w-3.5 h-3.5" /> BLE Beacon
              </span>
            ) : (
              <>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-medium text-sky-700 border-sky-300 bg-sky-50 dark:text-sky-300 dark:border-sky-800 dark:bg-sky-950/40" data-testid={`${kind}-gps-icon-${device.id}`}>
                  <Satellite className="w-3.5 h-3.5" /> GPS Tracker
                </span>
                <Badge variant="outline" className={`text-xs capitalize ${ASSET_STATUS_CLS[assetLiveStatus(device)]}`} data-testid={`${kind}-gps-status-${device.id}`}>{assetLiveStatus(device)}</Badge>
                {(device.last_seen || device.last_gps_time) && (
                  <span className="text-[10px] text-muted-foreground">{formatTimeAgo(device.last_seen || device.last_gps_time)}</span>
                )}
              </>
            )}
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Type</span>
            <Badge variant="outline" className="capitalize">{(device.asset_type || '').replace(/_/g, ' ')}</Badge>
          </div>
          {device.asset_make && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Make/Model</span>
              <span className="font-medium">{device.asset_make} {device.asset_model}</span>
            </div>
          )}
          {device.asset_year && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Year</span>
              <span className="font-medium">{device.asset_year}</span>
            </div>
          )}
          {device.serial_no && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Serial</span>
              <span className="font-mono text-xs">{device.serial_no}</span>
            </div>
          )}
          {!beacon && (device.odometer_source && device.odometer_source !== 'off' && device.computed_odometer != null) && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Odometer
                <span className="text-[10px] ml-1">({device.odometer_source === 'gps' ? 'GPS' : 'Sensor'})</span>
              </span>
              <span className="font-medium" data-testid={`${kind}-card-odometer-${device.id}`}>{formatDistance(device.computed_odometer, { decimals: 1 })}</span>
            </div>
          )}
          {!beacon && (device.engine_hours_source && device.engine_hours_source !== 'off' && device.computed_engine_hours != null) && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Engine Hours</span>
              <span className="font-medium">{Number(device.computed_engine_hours).toFixed(1)} h</span>
            </div>
          )}
          {beacon && attached && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Attached To</span>
              <span className="font-medium text-emerald-700 dark:text-emerald-400" data-testid={`${kind}-card-attached-${device.id}`}>{attached}</span>
            </div>
          )}
          {device.home_vehicle_name && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Home Vehicle</span>
              <span className="font-medium text-teal-600 dark:text-teal-400" data-testid={`${kind}-card-home-vehicle-${device.id}`}>{device.home_vehicle_name}</span>
            </div>
          )}
          {/* ── Beacon health — occupancy-style block under the badge row ── */}
          {beacon && (
            <div className="pt-2 border-t">
              <AssetBeaconHealthCompact health={assetHealth[device.id]} deviceId={device.id} />
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading && !hasLoadedOnce) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fleet-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <Truck className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Fleet Management</h1>
            <p className="text-sm text-muted-foreground">View and manage your allocated vehicles, trailers, and equipment</p>
          </div>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <MobileSectionMenu
        items={[
          hasVehicles && { id: 'vehicles', label: 'Vehicles', icon: Car },
          hasTrailers && { id: 'trailers', label: 'Trailers', icon: Package },
          hasEquipment && { id: 'equipment', label: 'Equipment', icon: Hammer },
          hasMaintenance && { id: 'maintenance', label: 'Maintenance', icon: Wrench },
          hasFuel && { id: 'fuel', label: 'Fuel', icon: FuelIcon },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={changeTab}
        testIdPrefix="fleet-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasVehicles && (
          <Button
            onClick={() => changeTab('vehicles')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'vehicles'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-vehicles"
          >
            <Car className="w-4 h-4" />
            Vehicles
          </Button>
        )}
        {hasTrailers && (
          <Button
            onClick={() => changeTab('trailers')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'trailers'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-trailers"
          >
            <Package className="w-4 h-4" />
            Trailers
          </Button>
        )}
        {hasEquipment && (
          <Button
            onClick={() => changeTab('equipment')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'equipment'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-equipment"
          >
            <Hammer className="w-4 h-4" />
            Equipment
          </Button>
        )}
        {hasMaintenance && (
          <Button
            onClick={() => changeTab('maintenance')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'maintenance'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-maintenance"
          >
            <Wrench className="w-4 h-4" />
            Maintenance
          </Button>
        )}
        {hasFuel && (
          <Button
            onClick={() => changeTab('fuel')}
            className={`gap-2 min-w-[170px] justify-center ${activeTab === 'fuel'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-fuel"
          >
            <FuelIcon className="w-4 h-4" />
            Fuel
          </Button>
        )}
      </div>

      {/* No Access */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don&apos;t have permission to access any sections of Fleet Management.</p>
          </CardContent>
        </Card>
      )}

      {/* ==================== VEHICLES TAB ==================== */}
      {activeTab === 'vehicles' && hasVehicles && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Vehicles</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Manage your allocated vehicles, their assigned devices, and beacon bindings.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(fleetCounts.vehicles > 0 || !!searchQuery) && (
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                    placeholder="Search vehicles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-10 pr-9 w-full"
                    data-testid="vehicles-search"
                  />
                  {searchQuery && (
                    <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="vehicles-clear-search">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
              {fleetCounts.vehicles > 0 && (<>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-8" data-testid="fleet-filters-button">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filters
                      {activeFilterCount > 0 && (
                        <>
                          <Badge className="ml-2 h-5 min-w-5 justify-center px-1.5 bg-[#2563eb] text-white hover:bg-[#2563eb]" data-testid="fleet-filters-count">{activeFilterCount}</Badge>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="Clear filters"
                            className="ml-1.5 -mr-1 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setBindFilter('all'); setStatusFilter('all'); setServiceFilter('all'); }}
                            data-testid="fleet-filters-clear"
                          >
                            <X className="w-3.5 h-3.5" />
                          </span>
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 max-h-[70vh] overflow-y-auto">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Status</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={statusFilter} onValueChange={setStatusFilter}>
                      {[['all', 'All statuses'], ['moving', 'Moving'], ['idle', 'Idle'], ['parked', 'Parked'], ['offline', 'Offline']].map(([v, l]) => (
                        <DropdownMenuRadioItem key={v} value={v} onSelect={(e) => e.preventDefault()} data-testid={`fleet-filter-status-${v}`}>{l}</DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Bindings</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={bindFilter} onValueChange={setBindFilter}>
                      {BIND_FILTERS.map((f) => (
                        <DropdownMenuRadioItem key={f.value} value={f.value} onSelect={(e) => e.preventDefault()} data-testid={`fleet-bind-filter-${f.value}`}>{f.value === 'all' ? 'All bindings' : f.label}</DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                    {hasMaintenance && (<>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">Service</DropdownMenuLabel>
                      <DropdownMenuRadioGroup value={serviceFilter} onValueChange={setServiceFilter}>
                        {[['all', 'All vehicles'], ['due_any', 'Service due (any)'], ['due_soon', 'Due soon'], ['overdue', 'Overdue']].map(([v, l]) => (
                          <DropdownMenuRadioItem key={v} value={v} onSelect={(e) => e.preventDefault()} data-testid={`fleet-filter-service-${v}`}>{l}</DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </>)}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Documents</DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={docsFilter} onValueChange={setDocsFilter}>
                      {[['all', 'All vehicles'], ['attention', 'Needs attention (any)'], ['expiring', 'Expiring ≤30 days'], ['expired', 'Expired']].map(([v, l]) => (
                        <DropdownMenuRadioItem key={v} value={v} onSelect={(e) => e.preventDefault()} data-testid={`fleet-filter-docs-${v}`}>{l}</DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                    {activeFilterCount > 0 && (<>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-xs text-muted-foreground justify-center"
                        onSelect={(e) => { e.preventDefault(); setBindFilter('all'); setStatusFilter('all'); setServiceFilter('all'); setDocsFilter('all'); }}
                        data-testid="fleet-filters-reset"
                      >
                        Reset filters
                      </DropdownMenuItem>
                    </>)}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Select value={fleetSort} onValueChange={setFleetSort}>
                  <SelectTrigger className="h-8 w-[160px]" data-testid="fleet-sort-select">
                    <ArrowUpDown className="w-4 h-4 mr-1" />
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name" data-testid="fleet-sort-name">Sort: Name</SelectItem>
                    <SelectItem value="last_seen" data-testid="fleet-sort-last_seen">Sort: Last seen</SelectItem>
                    <SelectItem value="odometer" data-testid="fleet-sort-odometer">Sort: Odometer</SelectItem>
                    {hasMaintenance && <SelectItem value="service_due" data-testid="fleet-sort-service_due">Sort: Service due</SelectItem>}
                  </SelectContent>
                </Select>
              </>)}
              <Button size="sm" variant="outline" onClick={() => { setBulkReadingsCategory('vehicle'); setBulkReadingsOpen(true); }} data-testid="vehicles-bulk-readings-button">
                <Gauge className="w-4 h-4 mr-2" />
                Update Readings
              </Button>
              <Button size="sm" variant="outline" disabled title="Coming Soon" data-testid="vehicles-import-button">
                <Download className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button size="sm"
                variant="outline"
                onClick={() => exportFleetCsv(vehicles, selectedVehicleIds, 'vehicles')}
                disabled={vehicles.length === 0}
                data-testid="vehicles-export-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {selectedVehicleIds.length > 0 ? `Export (${selectedVehicleIds.length})` : 'Export'}
              </Button>
              <Button size="sm" variant="outline" onClick={fetchFleetDevices} disabled={loading} data-testid="vehicles-refresh-button">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
          </div>

          {/* Count badge + binding-status pills on ONE row (Driver Corrections style).
              Reserves min-height matching a single Badge line so pills appearing/disappearing
              never shift the table below. */}
          <div className="flex flex-wrap items-center gap-2 min-h-[22px]" data-testid="vehicles-counts-row">
            <Badge variant="outline" data-testid="vehicles-total-badge">{fleetCounts.vehicles} Total</Badge>
            {fleetCounts.vehicles > 0 && fleetBindingSummary.bound > 0 && (
              [
                ['trailer', 'towing a trailer', Truck],
                ['equipment', 'carrying equipment', Container],
                ['passenger', 'with passengers', Users],
                ['staff', 'with staff', UserRound],
              ].map(([key, label, Icon]) => (
                fleetBindingSummary[key] > 0 ? (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setBindFilter(bindFilter === key ? 'all' : key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${bindFilter === key ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-muted/40 hover:bg-muted'}`}
                    data-testid={`fleet-binding-stat-${key}`}
                  >
                    <Icon className="w-3 h-3" />
                    {fleetBindingSummary[key]} {label}
                  </button>
                ) : null
              ))
            )}
          </div>

          {/* Always-visible selection bar (app-wide standard) */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="vehicles-selection-bar">
            <Checkbox
              checked={vehicles.length > 0 && vehicles.every(d => selectedVehicleIds.includes(d.id))}
              onCheckedChange={(checked) => {
                const pageIds = vehicles.map(d => d.id);
                setSelectedVehicleIds((prev) => checked
                  ? Array.from(new Set([...prev, ...pageIds]))
                  : prev.filter(id => !pageIds.includes(id)));
              }}
              aria-label="Select all vehicles on this page"
              data-testid="vehicles-page-select-all"
            />
            <span data-testid="vehicles-selection-count">{selectedVehicleIds.length} selected</span>
            {selectedVehicleIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
              disabled={selectAllAcrossLoading}
              onClick={async () => {
                if (selectedVehicleIds.length >= (fleetTotals.vehicles || 0)) { setSelectedVehicleIds([]); return; }
                try {
                  setSelectAllAcrossLoading(true);
                  const ids = await fetchAllFleetIds('vehicles', fleetTotals.vehicles);
                  setSelectedVehicleIds(ids);
                } catch { toast.error('Failed to select all vehicles'); }
                finally { setSelectAllAcrossLoading(false); }
              }}
              data-testid="vehicles-select-all-toggle">
              {selectAllAcrossLoading ? 'Selecting…' : (selectedVehicleIds.length >= (fleetTotals.vehicles || 0) ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedVehicleIds([])} data-testid="vehicles-selection-clear">Clear</Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[#2563eb]"
              onClick={() => {
                if (selectedVehicleIds.length > 50) { toast.error('Select up to 50 vehicles for bulk driver assignment'); return; }
                setBulkAssignOpen(true);
              }}
              data-testid="vehicles-bulk-driver-assignment-btn">
              <UserCog className="w-3 h-3 mr-1" /> Driver Assignment
            </Button>
            </>)}
            {!mob && (
              <div className="ml-auto flex items-center gap-0.5 rounded-md border p-0.5" data-testid="vehicles-view-toggle">
                <Button
                  variant={vehiclesView === 'cards' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-5 px-1.5"
                  onClick={() => changeVehiclesView('cards')}
                  title="Card view"
                  data-testid="vehicles-view-cards"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant={vehiclesView === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-5 px-1.5"
                  onClick={() => changeVehiclesView('list')}
                  title="List view"
                  data-testid="vehicles-view-list"
                >
                  <ListIcon className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>

          {vehicles.length === 0 ? (
            <Card>
              <CardContent>
                {(searchQuery || activeFilterCount > 0) ? (
                  <EmptyState icon={SlidersHorizontal} title="No vehicles match" description="No vehicles match the current search/filters. Clear the search or reset the filters." />
                ) : (
                  <EmptyState icon={Car} title="No vehicles allocated" description="Vehicles will appear here once allocated to you by an administrator." />
                )}
              </CardContent>
            </Card>
          ) : (!mob && vehiclesView === 'list') ? (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12" />
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Make / Model</TableHead>
                      <TableHead>
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleVehiclesSort('odometer')} data-testid="vehicles-sort-odometer">
                          Odometer
                          {vehiclesSort.key === 'odometer' ? (vehiclesSort.dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />) : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                        </button>
                      </TableHead>
                      <TableHead>Engine Hrs</TableHead>
                      <TableHead>
                        <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleVehiclesSort('driver')} data-testid="vehicles-sort-driver">
                          Driver
                          {vehiclesSort.key === 'driver' ? (vehiclesSort.dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />) : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                        </button>
                      </TableHead>
                      <TableHead>Attached</TableHead>
                      <TableHead>Status</TableHead>
                      {hasGreenDriving && <TableHead>Score</TableHead>}
                      <TableHead className="w-[130px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      if (!vehiclesSort.key) return vehicles;
                      const val = (d) => vehiclesSort.key === 'odometer'
                        ? ((d.odometer_source && d.odometer_source !== 'off' && d.computed_odometer != null) ? Number(d.computed_odometer) : null)
                        : ((currentDriverSessionFor(d)?.person_name || d.last_driver_session?.person_name || d.previous_driver_session?.person_name || '').toLowerCase() || null);
                      const dir = vehiclesSort.dir === 'asc' ? 1 : -1;
                      return [...vehicles].sort((a, b) => {
                        const av = val(a); const bv = val(b);
                        if (av == null && bv == null) return 0;
                        if (av == null) return 1;
                        if (bv == null) return -1;
                        return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
                      });
                    })().map((device) => {
                      const currentDriverSession = currentDriverSessionFor(device);
                      const lastDriverSession = device.last_driver_session || device.previous_driver_session;
                      const cb = device.current_bindings || {};
                      return (
                        <TableRow key={device.id} className={selectedVehicleIds.includes(device.id) ? 'bg-[#2563eb]/5' : ''} data-testid={`vehicle-list-row-${device.id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selectedVehicleIds.includes(device.id)}
                              onCheckedChange={(checked) => setSelectedVehicleIds((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                              aria-label={`Select ${deviceDisplayName(device)}`}
                              data-testid={`vehicle-select-${device.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => setQuickViewDevice(device)} className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" title="Open Vehicle Dashboard" data-testid={`vehicle-list-photo-btn-${device.id}`}>
                                {device.vehicle_photo?.url ? (
                                  <AuthPhotoThumb api={api} photo={device.vehicle_photo} className="w-9 h-9 rounded-md object-cover border shrink-0" testId={`vehicle-list-photo-${device.id}`}
                                    fallback={<DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'car'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />} />
                                ) : (
                                  <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'car'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />
                                )}
                              </button>
                              <div className="min-w-0">
                                <button type="button" className="font-medium text-left hover:text-[#2563eb] hover:underline" onClick={() => setQuickViewDevice(device)} data-testid={`vehicle-title-${device.id}`}>{deviceDisplayName(device)}</button>
                                <p className="text-xs text-muted-foreground font-mono truncate max-w-[160px]">{device.registration || device.imei}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{device.asset_type}</Badge></TableCell>
                          <TableCell className="text-sm">
                            {device.asset_make ? <span>{device.asset_make} {device.asset_model}{device.asset_year ? ` · ${device.asset_year}` : ''}</span> : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-sm" data-testid={`fleet-odometer-${device.id}`}>
                            {(device.odometer_source && device.odometer_source !== 'off' && device.computed_odometer != null)
                              ? formatDistance(device.computed_odometer, { decimals: 1 })
                              : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-sm" data-testid={`fleet-engine-hours-${device.id}`}>
                            {(device.engine_hours_source && device.engine_hours_source !== 'off' && device.computed_engine_hours != null)
                              ? `${Number(device.computed_engine_hours).toFixed(1)} h`
                              : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-sm" data-testid={`vehicle-list-driver-${device.id}`}>
                            {currentDriverSession?.person_name ? (
                              <OccupancyPill
                                rowType="driver"
                                name={currentDriverSession.person_name}
                                personType={currentDriverSession.person_type}
                                source={currentDriverSession.decision_source || currentDriverSession.credential_source}
                                onClick={() => openPersonSearch(currentDriverSession)}
                                testId={`fleet-current-driver-link-${device.id}`}
                              />
                            ) : device.manual_assigned_driver ? (
                              <span
                                className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                                title="Manually assigned default driver — a real RFID/BLE scan overrides it"
                                data-testid={`fleet-manual-driver-chip-${device.id}`}
                              >
                                <UserCog className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate max-w-[120px]">{device.manual_assigned_driver.person_name}</span>
                                <span className="text-[9px] uppercase opacity-70">Assigned</span>
                              </span>
                            ) : lastDriverSession?.person_name ? (
                              <span className="text-xs text-muted-foreground">Last: {lastDriverSession.person_name}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell data-testid={`vehicle-list-attached-${device.id}`}>
                            {(() => {
                              const items = ['trailer', 'equipment', 'passenger'].flatMap((key) =>
                                (cb[key] || []).map((o, i) => ({ o, key, i })));
                              if (items.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
                              return (
                                <div className="flex flex-wrap gap-1 max-w-[240px]">
                                  {items.map(({ o, key, i }) => (
                                    <OccupancyPill
                                      key={`${key}-${i}`}
                                      rowType={key}
                                      name={typeof o === 'string' ? o : o.name}
                                      personType={o.person_type}
                                      source={o.source}
                                      onClick={key === 'passenger' ? () => openPersonSearch({ person_name: typeof o === 'string' ? o : o.name, person_id: o?.person_id, person_type: o?.person_type || 'passenger' }) : undefined}
                                      testId={`vehicle-list-attached-${key}-${device.id}-${i}`}
                                    />
                                  ))}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Badge variant="outline" className={`text-xs ${device.is_active ? statusColors.active : statusColors.offline}`}>
                                {device.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <VehicleDocsChip device={device} summary={docsSummary} onClick={() => openDocsViewer(device)} />
                              {hasMaintenance && (
                                <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
                              )}
                            </div>
                          </TableCell>
                          {hasGreenDriving && (
                            <TableCell data-testid={`vehicle-score-cell-${device.id}`}>
                              <VehicleScoreBadge b={behaviourByDevice?.[device.id]} deviceId={device.id} />
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {hasTravelHistory && (
                                <Button size="sm" variant="outline" onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`vehicle-travel-history-button-${device.id}`}>
                                  <HistoryIcon className="w-3 h-3" />
                                </Button>
                              )}
                              <AssetActionsMenu
                                kind="vehicle"
                                device={device}
                                mob={false}
                                onDriverLogbook={(device.identity_logbook?.enabled || device.identity_logbook_has_history) ? () => openVehicleDriverLogbook(device, driverLogbookFilters) : null}
                                onAssetLogbook={(device.identity_logbook?.enabled || device.asset_logbook_has_history) ? () => setAssetLogbook({ open: true, device }) : null}
                                onEdit={() => openAssetEdit(device)}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((device) => {
                const currentDriverSession = currentDriverSessionFor(device);
                const lastDriverSession = device.last_driver_session || device.previous_driver_session;
                return (
                  <Card
                    key={device.id}
                    className={`hover:shadow-lg dark:hover:bg-slate-800/40 transition-all cursor-pointer ${selectedVehicleIds.includes(device.id) ? 'ring-2 ring-[#2563eb]' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('button, a, input, [role="checkbox"], [role="menuitem"], [role="dialog"]')) return;
                      setQuickViewDevice(device);
                    }}
                    data-testid={`vehicle-card-${device.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedVehicleIds.includes(device.id)}
                            onCheckedChange={(checked) => setSelectedVehicleIds((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                            aria-label={`Select ${deviceDisplayName(device)}`}
                            data-testid={`vehicle-select-${device.id}`}
                          />
                          {device.vehicle_photo?.url ? (
                            <AuthPhotoThumb api={api} photo={device.vehicle_photo} className="w-12 h-12 rounded-lg object-cover border" testId={`vehicle-card-photo-${device.id}`}
                              fallback={
                                <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                                  <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'car'} className="w-7 h-7" iconColor={device.device_icon_color} />
                                </div>
                              } />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center text-[#2563eb]">
                              <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'car'} className="w-7 h-7" iconColor={device.device_icon_color} />
                            </div>
                          )}
                          <div>
                            <CardTitle className="text-base" data-testid={`vehicle-title-${device.id}`}>{deviceDisplayName(device)}</CardTitle>
                            <p className="text-sm text-muted-foreground">{device.registration || device.imei}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {hasTravelHistory && (
                            <Button size="sm" variant="outline" className={mob ? 'h-9 w-9 p-0' : ''} onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`vehicle-travel-history-button-${device.id}`}>
                              <HistoryIcon className={mob ? '!w-5 !h-5' : 'w-3 h-3'} />
                            </Button>
                          )}
                          <AssetActionsMenu
                            kind="vehicle"
                            device={device}
                            mob={mob}
                            onDriverLogbook={(device.identity_logbook?.enabled || device.identity_logbook_has_history) ? () => openVehicleDriverLogbook(device, driverLogbookFilters) : null}
                            onAssetLogbook={(device.identity_logbook?.enabled || device.asset_logbook_has_history) ? () => setAssetLogbook({ open: true, device }) : null}
                            onEdit={() => openAssetEdit(device)}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* ── Status / Ignition / Documents / Score / Service badge row ── */}
                      <div className="pb-2 border-b flex flex-wrap items-center gap-1.5" data-testid={`vehicle-card-badges-${device.id}`}>
                        <Badge variant="outline" className={`text-xs ${device.is_active ? statusColors.active : statusColors.offline}`} data-testid={`vehicle-card-active-badge-${device.id}`}>
                          {device.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {(() => {
                          const ignOn = isIgnitionOn(device);
                          return (
                            <span
                              className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-medium ${ignOn ? 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:border-emerald-800 dark:bg-emerald-950/40' : 'text-muted-foreground border-slate-300 dark:border-slate-600'}`}
                              data-testid={`vehicle-card-ignition-${device.id}`}
                            >
                              <Key className="w-3.5 h-3.5" /> Ignition {ignOn ? 'On' : 'Off'}
                            </span>
                          );
                        })()}
                        <VehicleDocsChip device={device} summary={docsSummary} large={mob} onClick={() => openDocsViewer(device)} />
                        {hasMaintenance && (
                          <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} large={mob} onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
                        )}
                        {hasGreenDriving && <VehicleScoreBadge b={behaviourByDevice?.[device.id]} deviceId={device.id} testId={`vehicle-card-score-badge-${device.id}`} />}
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Type</span>
                        <Badge variant="outline" className="capitalize">{device.asset_type}</Badge>
                      </div>
                      {device.asset_make && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Make/Model</span>
                          <span className="font-medium">{device.asset_make} {device.asset_model}</span>
                        </div>
                      )}
                      {device.asset_year && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Year</span>
                          <span className="font-medium">{device.asset_year}</span>
                        </div>
                      )}
                      {device.vin_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">VIN</span>
                          <span className="font-mono text-xs">{device.vin_number}</span>
                        </div>
                      )}
                      {device.fuel_type && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Fuel</span>
                          <span className="font-medium capitalize">{device.fuel_type}</span>
                        </div>
                      )}
                      {(device.odometer_source && device.odometer_source !== 'off' && device.computed_odometer != null) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            Odometer
                            <span className="text-[10px] ml-1">({device.odometer_source === 'gps' ? 'GPS' : 'Sensor'})</span>
                          </span>
                          <span className="font-medium" data-testid={`fleet-odometer-${device.id}`}>
                            {formatDistance(device.computed_odometer, { decimals: 1 })}
                          </span>
                        </div>
                      )}
                      {(device.engine_hours_source && device.engine_hours_source !== 'off' && device.computed_engine_hours != null) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Engine Hours</span>
                          <span className="font-medium" data-testid={`fleet-engine-hours-${device.id}`}>
                            {Number(device.computed_engine_hours).toFixed(1)} h
                          </span>
                        </div>
                      )}
                      {/* ── Live Occupancy — unified, consistent badge presentation ── */}
                      {(() => {
                        const cb = device.current_bindings || {};
                        const lb = device.last_bindings || {};
                        const lastEndedAt = device.last_trip_ended_at || null;
                        const ignitionOn = isIgnitionOn(device);
                        const driverNames = currentDriverSession?.person_name ? [currentDriverSession.person_name] : [];
                        const present = [
                          { key: 'driver', names: driverNames },
                          { key: 'staff', names: cb.staff || [] },
                          { key: 'passenger', names: cb.passenger || [] },
                          { key: 'trailer', names: cb.trailer || [] },
                          { key: 'equipment', names: cb.equipment || [] },
                        ].filter((r) => r.names.length > 0).map((r) => ({ ...r, meta: BINDING_BADGE_META[r.key] }));
                        const hasLastOccupant = ['staff', 'passenger', 'trailer', 'equipment'].some((k) => (lb[k] || []).length > 0);
                        // "No Driver" only makes sense when the vehicle can actually identify
                        // drivers (Identity & Log Books enabled) — otherwise every moving
                        // vehicle would misleadingly show "No Driver".
                        const identityEnabled = !!device.identity_logbook?.enabled;
                        const showBlock = present.length > 0 || (ignitionOn && identityEnabled) || !!lastDriverSession?.person_name || hasLastOccupant || !!device.manual_assigned_driver;
                        if (!showBlock) return null;
                        return (
                          <div className="pt-2 border-t space-y-1.5" data-testid={`fleet-bindings-${device.id}`}>
                            <div className="flex items-center gap-2 text-sm font-semibold">
                              <Users className="w-4 h-4 text-[#2563eb]" /> Live Occupancy
                            </div>

                            {/* Driver row — shown when a driver is identified, or on trip
                                (ignition on) for identity-enabled vehicles only */}
                            {(driverNames.length > 0 || (ignitionOn && identityEnabled) || device.manual_assigned_driver) && (
                              <div className="flex items-start justify-between gap-2 text-xs" data-testid={`fleet-current-driver-${device.id}`}>
                                <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                                  <IdCard className="w-3 h-3" /> {BINDING_BADGE_META.driver.label}
                                </span>
                                <div className="flex flex-wrap justify-end gap-1">
                                  {currentDriverSession ? (
                                    <>
                                      <Button type="button" variant="outline" size="icon" className="h-6 w-6" title="Correct Driver" onClick={() => setDriverCorrectionDevice(device)} data-testid={`fleet-correct-driver-${device.id}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                      </Button>
                                      <OccupancyPill
                                        rowType="driver"
                                        name={currentDriverSession.person_name}
                                        personType={currentDriverSession.person_type}
                                        source={currentDriverSession.decision_source || currentDriverSession.credential_source}
                                        onClick={() => openPersonSearch(currentDriverSession)}
                                        testId={`fleet-current-driver-link-${device.id}`}
                                      />
                                    </>
                                  ) : device.manual_assigned_driver ? (
                                    <span
                                      className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                                      title="Manually assigned default driver — a real RFID/BLE scan overrides it"
                                      data-testid={`fleet-manual-driver-chip-${device.id}`}
                                    >
                                      <UserCog className="w-2.5 h-2.5 shrink-0" />
                                      <span className="truncate max-w-[120px]">{device.manual_assigned_driver.person_name}</span>
                                      <span className="text-[9px] uppercase opacity-70">Assigned</span>
                                    </span>
                                  ) : (
                                    <span
                                      className="inline-flex items-center gap-1 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                                      data-testid={`fleet-current-driver-empty-${device.id}`}
                                    >
                                      <UserX className="h-3 w-3" aria-hidden="true" data-testid={`fleet-current-driver-empty-icon-${device.id}`} />
                                      No Driver
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Last driver (muted) — only when no current driver. Mirrors the
                                Live Tracking Driver row: IdCard icon + "Driver" label, "Last:" prefix. */}
                            {driverNames.length === 0 && lastDriverSession?.person_name && (
                              <div className="flex items-start justify-between gap-2 text-xs" data-testid={`fleet-last-driver-${device.id}`}>
                                <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                                  <IdCard className="w-3 h-3" /> {BINDING_BADGE_META.driver.label}
                                </span>
                                <div className="flex flex-wrap justify-end items-center gap-1">
                                  <span className="text-[10px] text-muted-foreground">Last:</span>
                                  <OccupancyPill
                                    rowType="driver"
                                    name={lastDriverSession.person_name}
                                    personType={lastDriverSession.person_type}
                                    source={lastDriverSession.decision_source || lastDriverSession.credential_source}
                                    last
                                    onClick={() => openPersonSearch(lastDriverSession)}
                                    testId={`fleet-last-driver-link-${device.id}`}
                                  />
                                  {(lastDriverSession.ended_at || lastEndedAt) && (
                                    <span className="text-[9px] text-muted-foreground/70" title={formatDT(lastDriverSession.ended_at || lastEndedAt)}>· {formatTimeAgo(lastDriverSession.ended_at || lastEndedAt)}</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Staff / Passengers / Trailer / Equipment — consistent badge rows */}
                            {['staff', 'passenger', 'trailer', 'equipment'].map((key) => {
                              const meta = BINDING_BADGE_META[key];
                              const items = cb[key] || [];
                              const lastItems = lb[key] || [];
                              const showRow = items.length > 0 || lastItems.length > 0;
                              if (!showRow) return null;
                              const Icon = meta.icon;
                              return (
                                <div key={key} className="flex items-start justify-between gap-2 text-xs" data-testid={`fleet-binding-row-${key}-${device.id}`}>
                                  <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                                    <Icon className="w-3 h-3" /> {meta.label}
                                  </span>
                                  <div className="flex flex-wrap justify-end items-center gap-1">
                                    {items.length > 0 ? (
                                      <>
                                        {items.map((o, i) => (
                                          <OccupancyPill
                                            key={i}
                                            rowType={key}
                                            name={o.name}
                                            personType={o.person_type}
                                            source={o.source}
                                            stale={device.status === 'offline' && (key === 'trailer' || key === 'equipment')}
                                            staleNote={device.status === 'offline' && (key === 'trailer' || key === 'equipment') ? `Device offline — attachment last confirmed ${o.last_seen_at ? formatTimeAgo(o.last_seen_at) : 'before the device went offline'}` : undefined}
                                            onClick={(key === 'staff' || key === 'passenger') ? () => openPersonSearch({ person_name: o.name, person_id: o.person_id, person_type: o.person_type || key }) : undefined}
                                            testId={`fleet-binding-link-${key}-${device.id}-${i}`}
                                          />
                                        ))}
                                        {device.status === 'offline' && (key === 'trailer' || key === 'equipment') && items[0]?.last_seen_at && (
                                          <span className="text-[9px] text-muted-foreground/70" data-testid={`fleet-binding-stale-note-${key}-${device.id}`}>· last confirmed {formatTimeAgo(items[0].last_seen_at)}</span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="flex flex-wrap justify-end items-center gap-1" data-testid={`fleet-binding-last-${key}-${device.id}`}>
                                        <span className="text-[10px] text-muted-foreground">Last:</span>
                                        {lastItems.map((o, i) => (
                                          <OccupancyPill
                                            key={i}
                                            rowType={key}
                                            name={o.name}
                                            personType={o.person_type}
                                            source={o.source}
                                            last
                                            onClick={(key === 'staff' || key === 'passenger') ? () => openPersonSearch({ person_name: o.name, person_id: o.person_id, person_type: o.person_type || key }) : undefined}
                                            testId={`fleet-binding-last-link-${key}-${device.id}-${i}`}
                                          />
                                        ))}
                                        {lastEndedAt && (
                                          <span className="text-[9px] text-muted-foreground/70" title={formatDT(lastEndedAt)}>· {formatTimeAgo(lastEndedAt)}</span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          <PaginationFooter page={vehiclesPage} setPage={setVehiclesPage} totalItems={fleetTotals.vehicles || 0} itemsPerPage={fleetPageSize} setItemsPerPage={setFleetPageSize} testIdPrefix="vehicles" disabled={loading} alwaysShow />
        </div>
      )}

      {/* ==================== TRAILERS TAB ==================== */}
      {activeTab === 'trailers' && hasTrailers && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Trailers</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Manage trailers and their beacon bindings for automatic vehicle-trailer pairing.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(fleetCounts.trailers > 0 || !!searchQuery) && (
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search trailers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 pl-10 pr-9"
                  data-testid="trailers-search"
                />
                {searchQuery && (
                  <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="trailers-clear-search">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => { setBulkReadingsCategory('trailer'); setBulkReadingsOpen(true); }} data-testid="trailers-bulk-readings-button">
              <Gauge className="w-4 h-4 mr-2" />
              Update Readings
            </Button>
            <Button size="sm" variant="outline" disabled title="Coming Soon" data-testid="trailers-import-button">
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button size="sm"
              variant="outline"
              onClick={() => exportFleetCsv(trailers, selectedTrailerIds, 'trailers')}
              disabled={trailers.length === 0}
              data-testid="trailers-export-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {selectedTrailerIds.length > 0 ? `Export (${selectedTrailerIds.length})` : 'Export'}
            </Button>
            <Button size="sm" variant="outline" onClick={fetchFleetDevices} disabled={loading} data-testid="trailers-refresh-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
              </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="trailers-counts-row">
            <Badge variant="outline" data-testid="trailers-total-badge">{fleetCounts.trailers} Total</Badge>
          </div>

          {/* Always-visible selection bar (app-wide standard) */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="trailers-selection-bar">
            <Checkbox
              checked={trailers.length > 0 && trailers.every(d => selectedTrailerIds.includes(d.id))}
              onCheckedChange={(checked) => {
                const pageIds = trailers.map(d => d.id);
                setSelectedTrailerIds((prev) => checked
                  ? Array.from(new Set([...prev, ...pageIds]))
                  : prev.filter(id => !pageIds.includes(id)));
              }}
              aria-label="Select all trailers on this page"
              data-testid="trailers-page-select-all"
            />
            <span data-testid="trailers-selection-count">{selectedTrailerIds.length} selected</span>
            {selectedTrailerIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
              disabled={selectAllAcrossLoading}
              onClick={async () => {
                if (selectedTrailerIds.length >= (fleetTotals.trailers || 0)) { setSelectedTrailerIds([]); return; }
                try {
                  setSelectAllAcrossLoading(true);
                  const ids = await fetchAllFleetIds('trailers', fleetTotals.trailers);
                  setSelectedTrailerIds(ids);
                } catch { toast.error('Failed to select all trailers'); }
                finally { setSelectAllAcrossLoading(false); }
              }}
              data-testid="trailers-select-all-toggle">
              {selectAllAcrossLoading ? 'Selecting…' : (selectedTrailerIds.length >= (fleetTotals.trailers || 0) ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedTrailerIds([])} data-testid="trailers-selection-clear">Clear</Button>
            </>)}
            {!mob && (
              <div className="ml-auto flex items-center gap-0.5 rounded-md border p-0.5" data-testid="trailers-view-toggle">
                <Button variant={assetsViewByTab.trailers === 'cards' ? 'secondary' : 'ghost'} size="sm" className="h-5 px-1.5" onClick={() => changeAssetsView('trailers', 'cards')} title="Card view" data-testid="trailers-view-cards"><LayoutGrid className="w-3.5 h-3.5" /></Button>
                <Button variant={assetsViewByTab.trailers === 'list' ? 'secondary' : 'ghost'} size="sm" className="h-5 px-1.5" onClick={() => changeAssetsView('trailers', 'list')} title="List view" data-testid="trailers-view-list"><ListIcon className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          {trailers.length === 0 ? (
            <Card>
              <CardContent>
                {searchQuery ? (
                  <EmptyState icon={Package} title="No trailers match" description="No trailers match the current search. Clear the search to see all trailers." />
                ) : (
                  <EmptyState icon={Package} title="No trailers allocated" description="Trailers will appear here once allocated to you by an administrator." />
                )}
              </CardContent>
            </Card>
          ) : mob ? (
            <div className="space-y-3" data-testid="trailers-mobile-list">
              {trailers.map((device) => renderAssetMobileCard(device, 'trailer'))}
            </div>
          ) : assetsViewByTab.trailers === 'cards' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trailers-card-grid">
              {trailers.map((device) => renderAssetDesktopCard(device, 'trailer'))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12" />
                      <TableHead>Trailer</TableHead>
                      <TableHead>Tracker</TableHead>
                      <TableHead>Make / Model</TableHead>
                      <TableHead>Odometer</TableHead>
                      <TableHead>Engine Hrs</TableHead>
                      <TableHead>Health / Status</TableHead>
                      <TableHead>Attached To</TableHead>
                      <TableHead>Home Vehicle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trailers.map((device) => (
                      <TableRow key={device.id} className={selectedTrailerIds.includes(device.id) ? 'bg-[#2563eb]/5' : ''} data-testid={`trailer-row-${device.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedTrailerIds.includes(device.id)}
                            onCheckedChange={(checked) => setSelectedTrailerIds((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                            aria-label={`Select ${deviceDisplayName(device)}`}
                            data-testid={`trailer-select-${device.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-[230px] overflow-hidden">
                            <button type="button" onClick={() => setQuickViewDevice(device)} className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" title="Open Trailer Dashboard" data-testid={`trailer-list-photo-btn-${device.id}`}>
                              {device.vehicle_photo?.url ? (
                                <AuthPhotoThumb api={api} photo={device.vehicle_photo} className="w-9 h-9 rounded-md object-cover border shrink-0" testId={`trailer-list-photo-${device.id}`}
                                  fallback={<DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'trailer'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />} />
                              ) : (
                                <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'trailer'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />
                              )}
                            </button>
                            <div className="min-w-0 overflow-hidden">
                              <button type="button" className="font-medium text-left hover:text-[#2563eb] hover:underline block truncate max-w-[170px]" onClick={() => setQuickViewDevice(device)} data-testid={`trailer-title-${device.id}`}>
                                {deviceDisplayName(device)}
                              </button>
                              <p className="text-xs text-muted-foreground font-mono truncate max-w-[160px]">{device.registration || device.serial_no || device.name}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap" data-testid={`trailer-tracker-${device.id}`}>
                          {isAssetBeacon(device)
                            ? <Badge variant="outline" className="gap-1 text-xs text-teal-700 border-teal-300 bg-teal-50 dark:text-teal-300 dark:border-teal-800 dark:bg-teal-950/40" data-testid={`trailer-beacon-icon-${device.id}`}><Bluetooth className="w-3 h-3" /> BLE</Badge>
                            : <Badge variant="outline" className="gap-1 text-xs text-sky-700 border-sky-300 bg-sky-50 dark:text-sky-300 dark:border-sky-800 dark:bg-sky-950/40" data-testid={`trailer-gps-icon-${device.id}`}><Satellite className="w-3 h-3" /> GPS</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">
                          {device.asset_make ? <span>{device.asset_make} {device.asset_model}{device.asset_year ? ` · ${device.asset_year}` : ''}</span> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`trailer-odometer-${device.id}`}>
                          {(!isAssetBeacon(device) && device.odometer_source && device.odometer_source !== 'off' && device.computed_odometer != null)
                            ? formatDistance(device.computed_odometer, { decimals: 1 })
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`trailer-engine-hours-${device.id}`}>
                          {(!isAssetBeacon(device) && device.engine_hours_source && device.engine_hours_source !== 'off' && device.computed_engine_hours != null)
                            ? `${Number(device.computed_engine_hours).toFixed(1)} h`
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {renderAssetLiveCell(device, 'trailer')}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`trailer-attached-${device.id}`}>
                          {assetAttachedVehicle(device)
                            ? <span className="text-emerald-700 dark:text-emerald-400 font-medium">{assetAttachedVehicle(device)}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`trailer-home-vehicle-${device.id}`}>
                          {device.home_vehicle_name
                            ? <span className="text-teal-600 dark:text-teal-400 font-medium">{device.home_vehicle_name}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className={`text-xs ${device.is_active ? statusColors.active : statusColors.offline}`}>
                              {device.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <VehicleDocsChip device={device} summary={docsSummary} onClick={() => openDocsViewer(device)} />
                            {hasMaintenance && (
                              <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {hasTravelHistory && (
                              <Button size="sm" variant="outline" onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`trailer-travel-history-button-${device.id}`}>
                                <HistoryIcon className="w-3 h-3" />
                              </Button>
                            )}
                            <AssetActionsMenu kind="trailer" device={device} mob={false} onHitchLog={isAssetBeacon(device) ? () => setHitchLog({ open: true, device }) : null} onEdit={() => openAssetEdit(device)} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          <PaginationFooter page={trailersPage} setPage={setTrailersPage} totalItems={fleetTotals.trailers || 0} itemsPerPage={fleetPageSize} setItemsPerPage={setFleetPageSize} testIdPrefix="trailers" disabled={loading} alwaysShow />
        </div>
      )}

      {/* ==================== EQUIPMENT TAB ==================== */}
      {activeTab === 'equipment' && hasEquipment && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Hammer className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Equipment</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Manage equipment assets and their tracked serial numbers or beacon identifiers.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(fleetCounts.equipment > 0 || !!searchQuery) && (
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search equipment..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 pl-10 pr-9"
                  data-testid="equipment-search"
                />
                {searchQuery && (
                  <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="equipment-clear-search">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            <Button size="sm" variant="outline" onClick={() => { setBulkReadingsCategory('equipment'); setBulkReadingsOpen(true); }} data-testid="equipment-bulk-readings-button">
              <Gauge className="w-4 h-4 mr-2" />
              Update Readings
            </Button>
            <Button size="sm" variant="outline" disabled title="Coming Soon" data-testid="equipment-import-button">
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button size="sm"
              variant="outline"
              onClick={() => exportFleetCsv(equipment, selectedEquipmentIds, 'equipment')}
                disabled={equipment.length === 0}
                data-testid="equipment-export-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {selectedEquipmentIds.length > 0 ? `Export (${selectedEquipmentIds.length})` : 'Export'}
              </Button>
            <Button size="sm" variant="outline" onClick={fetchFleetDevices} disabled={loading} data-testid="equipment-refresh-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="equipment-counts-row">
            <Badge variant="outline" data-testid="equipment-total-badge">{fleetCounts.equipment} Total</Badge>
          </div>

          {/* Always-visible selection bar (app-wide standard) */}
          <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="equipment-selection-bar">
            {mob && (
              <Checkbox
                checked={equipment.length > 0 && equipment.every(d => selectedEquipmentIds.includes(d.id))}
                onCheckedChange={(checked) => {
                  const pageIds = equipment.map(d => d.id);
                  setSelectedEquipmentIds((prev) => checked
                    ? Array.from(new Set([...prev, ...pageIds]))
                    : prev.filter(id => !pageIds.includes(id)));
                }}
                aria-label="Select all equipment on this page"
                data-testid="equipment-page-select-all"
              />
            )}
            <span data-testid="equipment-selection-count">{selectedEquipmentIds.length} selected</span>
            {selectedEquipmentIds.length > 0 && (<>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
              disabled={selectAllAcrossLoading}
              onClick={async () => {
                if (selectedEquipmentIds.length >= (fleetTotals.equipment || 0)) { setSelectedEquipmentIds([]); return; }
                try {
                  setSelectAllAcrossLoading(true);
                  const ids = await fetchAllFleetIds('equipment', fleetTotals.equipment);
                  setSelectedEquipmentIds(ids);
                } catch { toast.error('Failed to select all equipment'); }
                finally { setSelectAllAcrossLoading(false); }
              }}
              data-testid="equipment-select-all-toggle">
              {selectAllAcrossLoading ? 'Selecting…' : (selectedEquipmentIds.length >= (fleetTotals.equipment || 0) ? 'Deselect All' : 'Select All')}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedEquipmentIds([])} data-testid="equipment-selection-clear">Clear</Button>
            </>)}
            {!mob && (
              <div className="ml-auto flex items-center gap-0.5 rounded-md border p-0.5" data-testid="equipment-view-toggle">
                <Button variant={assetsViewByTab.equipment === 'cards' ? 'secondary' : 'ghost'} size="sm" className="h-5 px-1.5" onClick={() => changeAssetsView('equipment', 'cards')} title="Card view" data-testid="equipment-view-cards"><LayoutGrid className="w-3.5 h-3.5" /></Button>
                <Button variant={assetsViewByTab.equipment === 'list' ? 'secondary' : 'ghost'} size="sm" className="h-5 px-1.5" onClick={() => changeAssetsView('equipment', 'list')} title="List view" data-testid="equipment-view-list"><ListIcon className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          {equipment.length === 0 ? (
            <Card>
              <CardContent>
                {searchQuery ? (
                  <EmptyState icon={Hammer} title="No equipment matches" description="No equipment matches the current search. Clear the search to see all equipment." />
                ) : (
                  <EmptyState icon={Hammer} title="No equipment allocated" description="Equipment will appear here once allocated to you by an administrator." />
                )}
              </CardContent>
            </Card>
          ) : mob ? (
            <div className="space-y-3" data-testid="equipment-mobile-list">
              {equipment.map((device) => renderAssetMobileCard(device, 'equipment'))}
              <PaginationFooter page={equipmentPage} setPage={setEquipmentPage} totalItems={fleetTotals.equipment || 0} itemsPerPage={fleetPageSize} setItemsPerPage={setFleetPageSize} testIdPrefix="equipment" disabled={loading} alwaysShow />
            </div>
          ) : assetsViewByTab.equipment === 'cards' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="equipment-card-grid">
                {equipment.map((device) => renderAssetDesktopCard(device, 'equipment'))}
              </div>
              <PaginationFooter page={equipmentPage} setPage={setEquipmentPage} totalItems={fleetTotals.equipment || 0} itemsPerPage={fleetPageSize} setItemsPerPage={setFleetPageSize} testIdPrefix="equipment" disabled={loading} alwaysShow />
            </>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={equipment.length > 0 && equipment.every((d) => selectedEquipmentIds.includes(d.id))}
                          onCheckedChange={(checked) => {
                            const pageIds = equipment.map(d => d.id);
                            setSelectedEquipmentIds((prev) => checked
                              ? Array.from(new Set([...prev, ...pageIds]))
                              : prev.filter(id => !pageIds.includes(id)));
                          }}
                          aria-label="Select all equipment on this page"
                          data-testid="equipment-select-all"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Tracker</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Make / Model</TableHead>
                      <TableHead>Odometer</TableHead>
                      <TableHead>Engine Hrs</TableHead>
                      <TableHead>Health / Status</TableHead>
                      <TableHead>Attached To</TableHead>
                      <TableHead>Home Vehicle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipment.map((device) => (
                      <TableRow key={device.id} data-testid={`equipment-row-${device.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedEquipmentIds.includes(device.id)}
                            onCheckedChange={(checked) => setSelectedEquipmentIds((prev) => checked ? [...prev, device.id] : prev.filter((id) => id !== device.id))}
                            aria-label={`Select ${deviceDisplayName(device)}`}
                            data-testid={`equipment-select-${device.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-[230px] overflow-hidden">
                            <button type="button" onClick={() => setQuickViewDevice(device)} className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" title="Open Equipment Dashboard" data-testid={`equipment-list-photo-btn-${device.id}`}>
                              {device.vehicle_photo?.url ? (
                                <AuthPhotoThumb api={api} photo={device.vehicle_photo} className="w-9 h-9 rounded-md object-cover border shrink-0" testId={`equipment-list-photo-${device.id}`}
                                  fallback={<DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'excavator'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />} />
                              ) : (
                                <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'excavator'} className="w-5 h-5 text-[#2563eb]" iconColor={device.device_icon_color} />
                              )}
                            </button>
                            <div className="min-w-0 overflow-hidden">
                              <button type="button" className="font-medium text-left hover:text-[#2563eb] hover:underline block truncate max-w-[170px]" onClick={() => setQuickViewDevice(device)} data-testid={`equipment-title-${device.id}`}>
                                {deviceDisplayName(device)}
                              </button>
                              <p className="text-xs text-muted-foreground font-mono truncate max-w-[160px]">{device.serial_no || device.container_number || device.serial_number || device.registration || device.name}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap" data-testid={`equipment-tracker-${device.id}`}>
                          {isAssetBeacon(device)
                            ? <Badge variant="outline" className="gap-1 text-xs text-teal-700 border-teal-300 bg-teal-50 dark:text-teal-300 dark:border-teal-800 dark:bg-teal-950/40" data-testid={`equipment-beacon-icon-${device.id}`}><Bluetooth className="w-3 h-3" /> BLE</Badge>
                            : <Badge variant="outline" className="gap-1 text-xs text-sky-700 border-sky-300 bg-sky-50 dark:text-sky-300 dark:border-sky-800 dark:bg-sky-950/40" data-testid={`equipment-gps-icon-${device.id}`}><Satellite className="w-3 h-3" /> GPS</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{(device.asset_type || '').replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {device.asset_make ? <span>{device.asset_make} {device.asset_model}{device.asset_year ? ` · ${device.asset_year}` : ''}</span> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`equipment-odometer-${device.id}`}>
                          {(!isAssetBeacon(device) && device.odometer_source && device.odometer_source !== 'off' && device.computed_odometer != null)
                            ? formatDistance(device.computed_odometer, { decimals: 1 })
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`equipment-engine-hours-${device.id}`}>
                          {(!isAssetBeacon(device) && device.engine_hours_source && device.engine_hours_source !== 'off' && device.computed_engine_hours != null)
                            ? `${Number(device.computed_engine_hours).toFixed(1)} h`
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {renderAssetLiveCell(device, 'equipment')}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`equipment-attached-${device.id}`}>
                          {assetAttachedVehicle(device)
                            ? <span className="text-emerald-700 dark:text-emerald-400 font-medium">{assetAttachedVehicle(device)}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-sm" data-testid={`equipment-home-vehicle-${device.id}`}>
                          {device.home_vehicle_name
                            ? <span className="text-teal-600 dark:text-teal-400 font-medium">{device.home_vehicle_name}</span>
                            : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className={`text-xs ${device.is_active ? statusColors.active : statusColors.offline}`}>
                              {device.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <VehicleDocsChip device={device} summary={docsSummary} onClick={() => openDocsViewer(device)} />
                            {hasMaintenance && (
                              <VehicleServiceChip device={device} status={svcStatusByDevice[device.id]} onClick={() => setUrlParams({ tab: 'maintenance', page: null, svc_device: device.id })} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {hasTravelHistory && (
                              <Button size="sm" variant="outline" onClick={() => openTravelHistory(device)} title="Travel History" data-testid={`equipment-travel-history-button-${device.id}`}>
                                <HistoryIcon className="w-3 h-3" />
                              </Button>
                            )}
                            <AssetActionsMenu kind="equipment" device={device} mob={false} onHitchLog={isAssetBeacon(device) ? () => setHitchLog({ open: true, device }) : null} onEdit={() => openAssetEdit(device)} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <PaginationFooter page={equipmentPage} setPage={setEquipmentPage} totalItems={fleetTotals.equipment || 0} itemsPerPage={fleetPageSize} setItemsPerPage={setFleetPageSize} testIdPrefix="equipment" disabled={loading} alwaysShow />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ==================== MAINTENANCE TAB ==================== */}
      {activeTab === 'maintenance' && hasMaintenance && (
        <MaintenanceTab />
      )}

      {/* ==================== FUEL TAB ==================== */}
      {activeTab === 'fuel' && hasFuel && (
        <FuelPage api={api} />
      )}

      <AttachmentsViewerDialog
        open={!!docsViewer}
        onOpenChange={(o) => { if (!o) setDocsViewer(null); }}
        title="Documents"
        subtitle={docsViewer ? deviceDisplayName(docsViewer.device) : ''}
        attachments={docsViewer?.attachments || []}
        testPrefix="vehicle-attachments-viewer"
      />
      <AssetLogbookDialog
        open={assetLogbook.open}
        onOpenChange={(open) => setAssetLogbook((p) => ({ ...p, open }))}
        device={assetLogbook.device}
        api={api}
      />

      <VehicleQuickView
        device={quickViewDevice && isVehicleType(quickViewDevice.asset_type) ? quickViewDevice : null}
        open={!!quickViewDevice && isVehicleType(quickViewDevice.asset_type)}
        onClose={() => setQuickViewDevice(null)}
        svcStatus={quickViewDevice && hasMaintenance ? svcStatusByDevice[quickViewDevice.id] : null}
        onEdit={quickViewDevice?.asset_editing_enabled !== false ? () => { const d = quickViewDevice; setQuickViewDevice(null); openAssetEdit(d); } : null}
        onOpenAssetLogbook={(quickViewDevice?.identity_logbook?.enabled || quickViewDevice?.asset_logbook_has_history)
          ? () => { const d = quickViewDevice; setQuickViewDevice(null); setAssetLogbook({ open: true, device: d }); }
          : null}
        onOpenDriverLogbook={(quickViewDevice?.identity_logbook?.enabled || quickViewDevice?.identity_logbook_has_history)
          ? () => { const d = quickViewDevice; setQuickViewDevice(null); openVehicleDriverLogbook(d, driverLogbookFilters); }
          : null}
        onOpenTravelHistory={hasTravelHistory ? () => { const d = quickViewDevice; setQuickViewDevice(null); openTravelHistory(d); } : null}
      />

      <AssetQuickView
        device={quickViewDevice && !isVehicleType(quickViewDevice.asset_type) ? quickViewDevice : null}
        open={!!quickViewDevice && !isVehicleType(quickViewDevice.asset_type)}
        onClose={() => setQuickViewDevice(null)}
        onEdit={quickViewDevice?.asset_editing_enabled !== false ? () => { const d = quickViewDevice; setQuickViewDevice(null); openAssetEdit(d); } : null}
        onOpenHitchLog={quickViewDevice && isAssetBeacon(quickViewDevice) ? () => { const d = quickViewDevice; setQuickViewDevice(null); setHitchLog({ open: true, device: d }); } : null}
        onOpenTravelHistory={hasTravelHistory ? () => { const d = quickViewDevice; setQuickViewDevice(null); openTravelHistory(d); } : null}
      />

      <BulkDriverAssignmentDialog
        api={api}
        open={bulkAssignOpen}
        onOpenChange={setBulkAssignOpen}
        deviceIds={selectedVehicleIds}
        devices={vehicles}
        onSaved={() => { setSelectedVehicleIds([]); fetchFleetDevices(true); }}
      />

      <AssetHitchLogDialog
        open={hitchLog.open}
        onOpenChange={(open) => setHitchLog((p) => ({ ...p, open }))}
        device={hitchLog.device}
        api={api}
      />

      <Dialog open={driverLogbookModal.open} onOpenChange={(open) => {
        setDriverLogbookModal(prev => ({ ...prev, open }));
        if (!open && driverLogbookReturnRef.current) {
          // Opened from a Person Log Book — return the user there on close.
          const ret = driverLogbookReturnRef.current;
          driverLogbookReturnRef.current = null;
          const ptype = ret.split(':')[0];
          const tab = ptype === 'staff' ? 'staff' : `${ptype}s`;
          navigate(`/people?tab=${tab}&open_person_logbook=${encodeURIComponent(ret)}`);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-driver-logbook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#2563eb]" /> Vehicle Driver Log Book</DialogTitle>
            <DialogDescription>Driver, staff &amp; passenger identity evidence from BLE beacon, iButton, and RFID signals. Trailer &amp; equipment now live in the Asset Log Book.</DialogDescription>
          </DialogHeader>
          {driverLogbookModal.loading ? (
            <div className="py-8 flex justify-center" data-testid="vehicle-driver-logbook-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : driverLogbookModal.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="vehicle-driver-logbook-error">{driverLogbookModal.error}</div>
          ) : driverLogbookModal.data ? (
            <div className="space-y-4" data-testid="vehicle-driver-logbook-content">
              {driverLogbookModal.data.identity_logbook && !driverLogbookModal.data.identity_logbook.enabled && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800/70 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="vehicle-driver-logbook-disabled-notice">
                  Identity Logbooks are disabled for new trips on this vehicle — showing historical data only.
                </div>
              )}
              <div className="rounded-lg border p-4 bg-background" data-testid="vehicle-driver-logbook-filters">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1"><Label>From</Label>
                    <div className="relative">
                    <Popover open={lbStartOpen} onOpenChange={setLbStartOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="vehicle-driver-logbook-start-filter">
                          <span className="truncate text-left min-w-0 flex-1">{driverLogbookFilters.start_time ? formatDT(driverLogbookFilters.start_time, { seconds: false }) : 'Start date'}</span>
                          {!driverLogbookFilters.start_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={driverLogbookFilters.start_time || undefined} onSelect={(date) => { if (!date) return; const prev = driverLogbookFilters.start_time; date.setHours(prev?.getHours() || 0, prev?.getMinutes() || 0, 0, 0); setDriverLogbookFilters((p) => ({ ...p, start_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={driverLogbookFilters.start_time ? `${String(driverLogbookFilters.start_time.getHours()).padStart(2, '0')}:${String(driverLogbookFilters.start_time.getMinutes()).padStart(2, '0')}` : '00:00'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setDriverLogbookFilters((p) => { const d = new Date(p.start_time || new Date()); d.setHours(h || 0, m || 0, 0, 0); return { ...p, start_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="vehicle-driver-logbook-start-time" />
                          {driverLogbookFilters.start_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDriverLogbookFilters((p) => ({ ...p, start_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setLbStartOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {driverLogbookFilters.start_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setDriverLogbookFilters((p) => ({ ...p, start_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="vehicle-driver-logbook-clear-start"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                  <div className="space-y-1"><Label>To</Label>
                    <div className="relative">
                    <Popover open={lbEndOpen} onOpenChange={setLbEndOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="vehicle-driver-logbook-end-filter">
                          <span className="truncate text-left min-w-0 flex-1">{driverLogbookFilters.end_time ? formatDT(driverLogbookFilters.end_time, { seconds: false }) : 'End date'}</span>
                          {!driverLogbookFilters.end_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={driverLogbookFilters.end_time || undefined} onSelect={(date) => { if (!date) return; const prev = driverLogbookFilters.end_time; date.setHours(prev?.getHours() || 23, prev?.getMinutes() || 59, 59, 999); setDriverLogbookFilters((p) => ({ ...p, end_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={driverLogbookFilters.end_time ? `${String(driverLogbookFilters.end_time.getHours()).padStart(2, '0')}:${String(driverLogbookFilters.end_time.getMinutes()).padStart(2, '0')}` : '23:59'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setDriverLogbookFilters((p) => { const d = new Date(p.end_time || new Date()); d.setHours(h || 0, m || 0, 59, 999); return { ...p, end_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="vehicle-driver-logbook-end-time" />
                          {driverLogbookFilters.end_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDriverLogbookFilters((p) => ({ ...p, end_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setLbEndOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {driverLogbookFilters.end_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setDriverLogbookFilters((p) => ({ ...p, end_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="vehicle-driver-logbook-clear-end"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <LogbookQuickRange
                    className="md:col-span-2"
                    startTime={driverLogbookFilters.start_time}
                    endTime={driverLogbookFilters.end_time}
                    onSelect={(start) => {
                      const next = { ...driverLogbookFilters, start_time: start, end_time: null };
                      setDriverLogbookFilters(next);
                      if (driverLogbookTarget) openVehicleDriverLogbook(driverLogbookTarget, next);
                    }}
                    testIdPrefix="vehicle-driver-logbook"
                  />
                  <div className="space-y-1"><Label>Source</Label><Select value={driverLogbookFilters.source} onValueChange={(v) => setDriverLogbookFilters(prev => ({ ...prev, source: v }))}><SelectTrigger data-testid="vehicle-driver-logbook-source-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all" data-testid="vehicle-driver-logbook-source-all">All</SelectItem><SelectItem value="rfid_ibutton" data-testid="vehicle-driver-logbook-source-rfid">RFID / iButton</SelectItem><SelectItem value="ble" data-testid="vehicle-driver-logbook-source-ble">BLE Beacon</SelectItem><SelectItem value="manager_assignment" data-testid="vehicle-driver-logbook-source-manager">Manager assignment</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1"><Label>Event</Label><Select value={driverLogbookFilters.event_type} onValueChange={(v) => setDriverLogbookFilters(prev => ({ ...prev, event_type: v }))}><SelectTrigger data-testid="vehicle-driver-logbook-event-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Trip">Trips</SelectItem><SelectItem value="Mid-trip Driver Scan Ignored">Ignored Mid-trip Scan</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1"><Label>Person Type</Label><Select value={driverLogbookFilters.person_type} onValueChange={(v) => setDriverLogbookFilters(prev => ({ ...prev, person_type: v }))}><SelectTrigger data-testid="vehicle-driver-logbook-person-type-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="driver">Driver</SelectItem><SelectItem value="staff">Staff</SelectItem><SelectItem value="passenger">Passenger</SelectItem></SelectContent></Select></div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_auto]">
                  <div className="relative">
                    <Input value={driverLogbookFilters.search} onChange={(e) => setDriverLogbookFilters(prev => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyVehicleDriverLogbookFilters(); } }} placeholder="Search person, credential, event" className="h-8 pr-9" data-testid="vehicle-driver-logbook-search-filter" />
                    {driverLogbookFilters.search && <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setDriverLogbookFilters(prev => ({ ...prev, search: '' }))} data-testid="vehicle-driver-logbook-clear-search-button"><X className="w-4 h-4" /></Button>}
                  </div>
                  <Button onClick={applyVehicleDriverLogbookFilters} data-testid="vehicle-driver-logbook-apply-filters-button"><Search className="w-4 h-4 mr-1" /> Search</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="vehicle-driver-logbook-export-button"><Upload className="w-4 h-4 mr-1" /> Export{selectedTrips.size ? ` (${selectedTrips.size})` : ''}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportAllTrips('csv')} data-testid="vehicle-driver-logbook-export-csv">CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAllTrips('pdf')} data-testid="vehicle-driver-logbook-export-pdf">PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEmailTrips()} disabled={!isNotificationChannelActive('notification_email')} data-testid="vehicle-driver-logbook-export-email"><Mail className="w-3.5 h-3.5 mr-2" /> Email PDF{!isNotificationChannelActive('notification_email') && <span className="ml-auto text-[10px] text-destructive">Restricted</span>}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="rounded-lg border p-4 bg-muted/20 grid gap-3 md:grid-cols-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Vehicle</p><p className="font-semibold">{driverLogbookModal.data.vehicle?.name}</p></div>
                <div><p className="text-xs text-muted-foreground">Trips</p><p className="font-semibold" data-testid="vehicle-driver-logbook-trip-count">{tripsTotal}</p></div>
                <div><p className="text-xs text-muted-foreground">Sources</p><p data-testid="vehicle-driver-logbook-sources">{(driverLogbookModal.data.source_labels || Array.from(new Set((driverLogbookModal.data.trips || []).flatMap(t => (t.occupants || []).map(o => o.source_label).filter(Boolean))))).join(', ') || '—'}</p></div>
              </div>
              {(driverLogbookModal.data.hints || []).map((hint, idx) => <div key={idx} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200/90">{hint}</div>)}
              {(driverLogbookModal.data.trips || []).length > 0 && (() => {
                const pageTrips = driverLogbookModal.data.trips || [];
                const pageAllOn = pageTrips.length > 0 && pageTrips.every((t) => selectedTrips.has(t.id));
                return (
                <div ref={driverLogbookTripsRef} className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground pl-[15px]">
                    <Checkbox
                      checked={pageAllOn}
                      onCheckedChange={(checked) => setSelectedTrips((prev) => {
                        const next = new Set(prev);
                        pageTrips.forEach((t) => { if (checked) next.add(t.id); else next.delete(t.id); });
                        return next;
                      })}
                      data-testid="vehicle-driver-logbook-select-all"
                    />
                    <span data-testid="vehicle-driver-logbook-selection-count">{selectedTrips.size} selected</span>
                    {selectedTrips.size > 0 && (<>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={async () => {
                        if (selectedTrips.size >= tripsTotal) { setSelectedTrips(new Set()); return; }
                        try { const ids = await fetchAllDriverLogbookTripIds(); setSelectedTrips(new Set(ids)); }
                        catch { toast.error('Could not select all trips'); }
                      }} data-testid="vehicle-driver-logbook-select-all-toggle">
                        {selectedTrips.size >= tripsTotal ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedTrips(new Set())} data-testid="vehicle-driver-logbook-clear-selection">Clear</Button>
                    </>)}
                  </div>
                  <Button variant="outline" size="sm" onClick={applyVehicleDriverLogbookFilters} data-testid="vehicle-driver-logbook-refresh-button">
                    <RefreshCw className={`w-4 h-4 mr-2 ${driverLogbookModal.loading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
                );
              })()}
              <div className="space-y-4">
              {(driverLogbookModal.data.trips || []).map((trip, i) => {
                const idx = tripPage * tripsPageSize + i;
                const roleKey = (r) => String(r || '').toLowerCase();
                const roleLabel = (r) => { const k = roleKey(r); return k ? k.charAt(0).toUpperCase() + k.slice(1) : 'Occupant'; };
                return (
                  <div key={trip.id || idx} className={`rounded-xl border p-3.5 space-y-3 bg-white dark:bg-slate-800/50 shadow-[0_2px_8px_rgba(15,23,42,0.08)] dark:shadow-none transition-shadow ${selectedTrips.has(trip.id) ? 'border-[#2563eb] ring-2 ring-[#2563eb]/40' : 'border-slate-300 dark:border-slate-700 hover:shadow-md'}`} data-testid={`vehicle-driver-logbook-trip-${idx}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <Checkbox checked={selectedTrips.has(trip.id)} onCheckedChange={() => toggleTripSelection(trip.id)} className="mt-1" data-testid={`vehicle-driver-logbook-trip-select-${idx}`} />
                        <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Route className="w-4 h-4 text-[#2563eb] shrink-0" />
                          <p className="font-semibold text-sm" data-testid={`vehicle-driver-logbook-trip-time-${idx}`}>
                            {formatDT(trip.started_at, { seconds: true })} <span className="text-muted-foreground font-normal">{'\u2192'}</span> {trip.is_active ? 'Now' : formatDT(trip.ended_at, { seconds: true })}
                          </p>
                          {trip.is_active ? (
                            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`vehicle-driver-logbook-trip-active-${idx}`}>Ongoing</Badge>
                          ) : null}
                          {trip.qualification?.label && (
                            <Badge variant="outline" className="text-[10px] border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300" data-testid={`vehicle-driver-logbook-trip-qualified-${idx}`}>{trip.qualification.label}</Badge>
                          )}
                          {trip.short_trip && (
                            <Badge variant="outline" className="text-[10px] border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300" data-testid={`vehicle-driver-logbook-trip-short-${idx}`}>Short trip</Badge>
                          )}
                        </div>
                        {(tripLocMapsUrl(trip.start_location) || tripLocMapsUrl(trip.end_location)) && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-[11px]" data-testid={`vehicle-driver-logbook-trip-locations-${idx}`}>
                            {tripLocMapsUrl(trip.start_location) && (
                              <a href={tripLocMapsUrl(trip.start_location)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open trip start in Google Maps" data-testid={`vehicle-driver-logbook-trip-start-loc-${idx}`}>
                                <MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">Start:</span> {fmtCoords(trip.start_location)}
                              </a>
                            )}
                            {tripLocMapsUrl(trip.end_location) && (
                              <a href={tripLocMapsUrl(trip.end_location)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open trip end in Google Maps" data-testid={`vehicle-driver-logbook-trip-end-loc-${idx}`}>
                                <MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">End:</span> {fmtCoords(trip.end_location)}
                              </a>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                        {trip.duration_seconds != null && (
                          <p className="flex items-center justify-end gap-1" data-testid={`vehicle-driver-logbook-trip-duration-${idx}`}><Clock className="w-3 h-3" /> {formatDuration(trip.duration_seconds)}</p>
                        )}
                        {trip.distance_km != null && (
                          <p className="flex items-center justify-end gap-1" data-testid={`vehicle-driver-logbook-trip-distance-${idx}`}><Route className="w-3 h-3" /> {formatDistance(trip.distance_km, { decimals: 1 })}</p>
                        )}
                        <div className="flex items-center justify-end gap-1 pt-0.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[11px]"
                            onClick={() => { logTripExport('csv', 1); exportTripCsv(trip, driverLogbookModal.data.vehicle?.name, idx); }}
                            data-testid={`vehicle-driver-logbook-trip-csv-${idx}`}
                          >
                            <Upload className="w-3 h-3 mr-1" /> CSV
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[11px]"
                            onClick={() => { logTripExport('pdf', 1); exportTripPdf(trip, driverLogbookModal.data.vehicle?.name, idx).catch(() => toast.error('Could not generate PDF')); }}
                            data-testid={`vehicle-driver-logbook-trip-pdf-${idx}`}
                          >
                            <Upload className="w-3 h-3 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                    {trip.pending_driver_ballot && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50/95 text-amber-900 dark:border-amber-800/70 dark:bg-amber-950/50 dark:text-amber-100 px-3 py-2.5 text-xs" data-testid={`vehicle-driver-logbook-trip-pending-${idx}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold flex items-center gap-1.5" data-testid={`vehicle-driver-logbook-pending-ballot-title-${idx}`}>
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              {trip.pending_driver_ballot.manager_correction_available
                                ? 'Automatically assigned Driver — correction available'
                                : trip.pending_driver_ballot.manager_review_required
                                ? 'No Driver — Manager review required'
                                : trip.is_active ? 'Driver confirmation pending' : 'Completed trip needs driver confirmation'}
                            </p>
                            <p className="mt-0.5 text-amber-800/85 dark:text-amber-100/80" data-testid={`vehicle-driver-logbook-pending-ballot-time-${idx}`}>
                              Ballot sent {formatDT(trip.pending_driver_ballot.ballot_sent_at, { seconds: true })}
                            </p>
                          </div>
                          <Badge variant="outline" className="border-amber-400 bg-white/70 text-amber-900 dark:bg-amber-950/70 dark:text-amber-100" data-testid={`vehicle-driver-logbook-trip-pending-review-${idx}`}>
                            {trip.pending_driver_ballot.manager_correction_available ? 'Auto-assigned' : trip.pending_driver_ballot.manager_review_required ? 'Review required' : 'Pending ballot'}
                          </Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`vehicle-driver-logbook-pending-ballot-candidates-${idx}`}>
                          {(trip.pending_driver_ballot.candidates || []).map((candidate, ci) => (
                            <span
                              key={candidate.person_id || ci}
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium ${candidate.response === 'rejected' ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300' : 'border-slate-300 bg-slate-100 text-slate-500 opacity-75 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-400'}`}
                              data-testid={`vehicle-driver-logbook-pending-candidate-${idx}-${ci}`}
                              title={candidate.response === 'rejected' ? 'Candidate declined' : 'Candidate is pending until the ballot is resolved'}
                            >
                              <UserRound className="w-3 h-3" />
                              {candidate.person_name || 'Unknown candidate'}
                              {candidate.person_type && <span className="uppercase text-[9px] tracking-wide text-slate-400">{candidate.person_type}</span>}
                              {candidate.response === 'rejected' && <span className="uppercase text-[9px] font-semibold">Declined</span>}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-amber-400 bg-white/80 px-2 text-[11px] text-amber-950 hover:bg-amber-100 dark:bg-amber-950/70 dark:text-amber-100"
                            onClick={() => openDriverBallot(trip.pending_driver_ballot.id)}
                            data-testid={`vehicle-driver-logbook-open-ballot-${idx}`}
                          >
                            <IdCard className="w-3 h-3 mr-1" /> {trip.pending_driver_ballot.manager_correction_available ? 'Review assignment' : trip.pending_driver_ballot.manager_review_required ? 'Review Driver' : 'Open ballot'}
                          </Button>
                        </div>
                      </div>
                    )}
                    {(trip.occupants || []).length === 0 ? (
                      trip.no_driver && !trip.pending_driver_ballot ? (
                        <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2.5 py-2 flex items-center gap-2" data-testid={`vehicle-driver-logbook-no-driver-${idx}`}>
                          <UserRound className="w-3.5 h-3.5 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground font-medium">{trip.short_trip ? 'Short trip — No driver identified' : 'No driver identified'}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No occupants recorded for this trip.</p>
                      )
                    ) : (
                      <div className="space-y-2">
                        {(trip.occupants || []).map((occ, oi) => {
                          const rk = roleKey(occ.role || occ.person_type);
                          const meta = BINDING_BADGE_META[rk] || { icon: UserRound, cls: '' };
                          const Icon = meta.icon || UserRound;
                          const actingDriverStaff = rk === 'driver' && String(occ.person_type || '').toLowerCase() === 'staff';
                          return (
                            <div key={occ.id || oi} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2.5 py-2" data-testid={`vehicle-driver-logbook-occupant-${idx}-${oi}`}>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`} data-testid={`vehicle-driver-logbook-occupant-role-${idx}-${oi}`}>
                                    <Icon className="w-3 h-3" /> {roleLabel(occ.role || occ.person_type)}
                                    {actingDriverStaff && <ShieldCheck className="w-3 h-3" data-testid={`vehicle-driver-logbook-occupant-driver-eligible-${idx}-${oi}`} />}
                                  </span>
                                  <span className="font-medium truncate" data-testid={`vehicle-driver-logbook-occupant-name-${idx}-${oi}`}>{occ.person_name || occ.resolved_person_name || occ.entity_display_name || 'Unmatched'}</span>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                  Boarded {formatDT(occ.started_at, { seconds: true })}
                                  {occ.is_active ? ' · still onboard' : <> {'\u2192'} Left {formatDT(occ.ended_at, { seconds: true })}</>}
                                </p>
                                {occ.is_ble && (occ.evidence?.bind_rssi != null || occ.evidence?.median_rssi != null) && (
                                  <p className="text-[11px] text-muted-foreground mt-0.5 font-mono" data-testid={`vehicle-driver-logbook-occupant-rssi-${idx}-${oi}`}>
                                    RSSI bind {occ.evidence?.bind_rssi != null ? `${occ.evidence.bind_rssi} dBm` : '—'}
                                    {' · '}last {occ.evidence?.median_rssi != null ? `${occ.evidence.median_rssi} dBm` : '—'}
                                  </p>
                                )}
                                {occ.role_transition ? (
                                  <p className="text-[11px] text-[#2563eb] dark:text-blue-300 mt-0.5" data-testid={`vehicle-driver-logbook-role-transition-${idx}-${oi}`}>
                                    {roleTransitionLabel(occ.role_transition)}
                                  </p>
                                ) : occ.allocation_note && (
                                  <p className="text-[11px] text-[#2563eb] dark:text-blue-300 mt-0.5" data-testid={`vehicle-driver-logbook-allocation-note-${idx}-${oi}`}>
                                    {occ.allocation_note}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                                <Badge variant="outline" className="flex items-center gap-1">
                                  {occ.is_ble ? <Bluetooth className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                                  {occ.source_label}
                                </Badge>
                                <span className="font-mono break-all">{occ.credential_identifier || '-'}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(trip.rejected_scans || []).length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200/90 px-2.5 py-2 text-xs" data-testid={`vehicle-driver-logbook-rejected-${idx}`}>
                        <p className="font-medium flex items-center gap-1"><Ban className="w-3 h-3" /> Rejected scans ({trip.rejected_scans.length})</p>
                        <ul className="mt-1 space-y-0.5">
                          {trip.rejected_scans.map((rj, ri) => (
                            <li key={ri} className="break-all" data-testid={`vehicle-driver-logbook-rejected-${idx}-${ri}`}>
                              <span className="font-medium">{rj.person_name || 'Unknown'}</span> · <span className="font-mono">{rj.credential_identifier || '-'}</span> <span className="text-amber-600 dark:text-amber-400/80">({formatDT(rj.timestamp, { seconds: true })})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
              <PaginationFooter
                page={tripPage}
                setPage={setTripPage}
                totalItems={tripsTotal}
                itemsPerPage={tripsPageSize}
                setItemsPerPage={setTripsPageSize}
                testIdPrefix="vehicle-driver-logbook"
                zeroIndexed
                disabled={driverLogbookModal.loading}
                scrollTargetRef={driverLogbookTripsRef}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ==================== ASSET EDIT DIALOG ==================== */}
      <BulkReadingsDialog open={bulkReadingsOpen} onOpenChange={setBulkReadingsOpen} onSaved={fetchFleetDevices} initialCategory={bulkReadingsCategory} />
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { if (!open) { setEditingDevice(null); } setIsEditDialogOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="asset-edit-dialog-title">
              Edit Asset Details
            </DialogTitle>
            <DialogDescription>
              {editingDevice ? `${deviceDisplayName(editingDevice)} - ${assetTypeLabel(editingDevice.asset_type)}` : ''}
            </DialogDescription>
          </DialogHeader>
          {editingDevice && (
            <div className="space-y-4 py-2">
              {/* Common fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Asset Name</Label>
                  <Input
                    data-testid="asset-name-input"
                    value={assetForm.asset_name}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, asset_name: e.target.value }))}
                    placeholder="e.g. Company Car #1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={assetForm.asset_description}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, asset_description: e.target.value }))}
                    placeholder="Additional details"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Asset Type</Label>
                <Select
                  value={assetForm.asset_type}
                  onValueChange={(v) => setAssetForm(prev => ({ ...prev, asset_type: v }))}
                >
                  <SelectTrigger data-testid="asset-type-select"><SelectValue placeholder="Select asset type" /></SelectTrigger>
                  <SelectContent>
                    {(editingDevice.device_type === 'beacon' ? beaconAssetTypes : assetTypes).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Photo — vehicles, trailers and equipment all support a display photo */}
              <div className="space-y-2">
                    <Label>{isVehicleType(editingDevice.asset_type) ? 'Vehicle Photo' : 'Asset Photo'}</Label>
                    <div className="flex items-center gap-3">
                      {editingDevice.vehicle_photo
                        ? <AuthPhotoThumb api={api} photo={editingDevice.vehicle_photo}
                            fallback={<div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center text-muted-foreground" data-testid="vehicle-photo-empty"><Package className="w-5 h-5 opacity-40" /></div>} />
                        : <div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center text-muted-foreground" data-testid="vehicle-photo-empty"><Package className="w-5 h-5 opacity-40" /></div>}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" id="vehicle-photo-input" onChange={handleVehiclePhotoUpload} data-testid="vehicle-photo-input" />
                      <Button type="button" size="sm" variant="outline" disabled={photoBusy} onClick={() => document.getElementById('vehicle-photo-input')?.click()} data-testid="vehicle-photo-upload-button">
                        {photoBusy ? 'Working…' : (editingDevice.vehicle_photo ? 'Replace Photo' : 'Upload Photo')}
                      </Button>
                      {editingDevice.vehicle_photo && (
                        <Button type="button" size="sm" variant="ghost" className="text-red-600" disabled={photoBusy} onClick={handleVehiclePhotoRemove} data-testid="vehicle-photo-remove-button">Remove</Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">JPEG/PNG/WEBP, max 5 MB. Any size or aspect ratio works — after choosing a file you can drag and zoom to pick the square section that is shown (saved as a 640×640 crop). Replacing or removing deletes the old file from storage.</p>
              </div>
              <PhotoCropDialog open={!!cropSrc} src={cropSrc} busy={photoBusy} onCancel={closeCrop} onCropped={handleCroppedPhoto} />

              {/* Vehicle-specific fields */}
              {isVehicleType(editingDevice.asset_type) && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Make</Label>
                      <Input
                        value={assetForm.asset_make}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_make: e.target.value }))}
                        placeholder="Toyota"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Input
                        value={assetForm.asset_model}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_model: e.target.value }))}
                        placeholder="Hilux"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Input
                        value={assetForm.asset_year}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_year: e.target.value }))}
                        placeholder="2024"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Registration</Label>
                      <Input
                        value={assetForm.registration}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, registration: e.target.value }))}
                        placeholder="ABC-123"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>VIN</Label>
                      <Input
                        value={assetForm.vin_number}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, vin_number: e.target.value }))}
                        placeholder="Vehicle Identification Number"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Fuel Type</Label>
                      <Select value={assetForm.fuel_type || ''} onValueChange={(v) => setAssetForm(prev => ({ ...prev, fuel_type: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select fuel type" />
                        </SelectTrigger>
                        <SelectContent>
                          {fuelTypes.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {editingDevice.odometer_source && editingDevice.odometer_source !== 'off' && (
                    <div className="space-y-2">
                      <Label>Odometer (km)</Label>
                      <Input
                        type="number"
                        value={assetForm.odometer_reading}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, odometer_reading: e.target.value }))}
                        placeholder="0"
                        disabled={editingDevice.odometer_source !== 'gps'}
                        data-testid="fleet-edit-odometer-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {editingDevice.odometer_source === 'gps' && 'Editable — calculated from GPS data'}
                        {editingDevice.odometer_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                      </p>
                    </div>
                    )}
                    {editingDevice.engine_hours_source && editingDevice.engine_hours_source !== 'off' && (
                    <div className="space-y-2">
                      <Label>Engine Hours</Label>
                      <Input
                        type="number"
                        value={assetForm.engine_hours}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, engine_hours: e.target.value }))}
                        placeholder="0"
                        disabled={editingDevice.engine_hours_source !== 'ignition'}
                        data-testid="fleet-edit-engine-hours-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {editingDevice.engine_hours_source === 'ignition' && 'Editable — counted when ignition is ON'}
                        {editingDevice.engine_hours_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                      </p>
                    </div>
                    )}
                  </div>
                  {/* ── Fuel configuration — shown when the admin enabled a Fuel Source ── */}
                  {editingDevice.device_type !== 'beacon' && editingDevice.fuel_source && editingDevice.fuel_source !== 'off' && (
                    <div className="p-3 border rounded-lg space-y-3" data-testid="edit-fuel-section">
                      <p className="font-medium text-sm">Fuel {editingDevice.fuel_source === 'sensor' ? '(Sensor)' : '(Estimate)'}</p>
                      {editingDevice.fuel_source === 'sensor' && ((editingDevice.sensor_values?.sensor_fuel_level ?? editingDevice.sensor_values?.sensor_ev_soc) != null) && (
                        <div className="p-2 bg-muted rounded-lg flex items-center justify-between text-sm" data-testid="fleet-edit-fuel-current-level">
                          <span className="text-muted-foreground">Current level</span>
                          <span className="font-medium">
                            {editingDevice.sensor_values?.sensor_fuel_level ?? editingDevice.sensor_values?.sensor_ev_soc}
                            {(() => { const m = (editingDevice.io_mappings || []).find(x => x.io_type === 'Fuel Level' || x.io_type === 'EV Battery Level'); const u = m?.units; return u && u !== 'None' ? ` ${u}` : ''; })()}
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        {assetForm.fuel_type !== 'electric' && (
                          <div className="space-y-2">
                            <Label>Tank Capacity (L)</Label>
                            <Input type="number" min="0" step="0.1" data-testid="fleet-edit-tank-capacity-input"
                              value={assetForm.tank_capacity}
                              onChange={(e) => setAssetForm(prev => ({ ...prev, tank_capacity: e.target.value }))}
                              placeholder="e.g. 60" autoComplete="off" />
                          </div>
                        )}
                        {(assetForm.fuel_type === 'electric' || assetForm.fuel_type === 'hybrid') && (
                          <div className="space-y-2">
                            <Label>Battery Capacity (kWh)</Label>
                            <Input type="number" min="0" step="0.1" data-testid="fleet-edit-battery-capacity-input"
                              value={assetForm.battery_capacity_kwh}
                              onChange={(e) => setAssetForm(prev => ({ ...prev, battery_capacity_kwh: e.target.value }))}
                              placeholder="e.g. 64" autoComplete="off" />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label>Reference Consumption ({assetForm.fuel_type === 'electric' ? 'kWh/100km' : 'L/100km'})</Label>
                          <Input type="number" min="0" step="0.1" data-testid="fleet-edit-reference-consumption-input"
                            value={assetForm.fuel_reference_consumption}
                            onChange={(e) => setAssetForm(prev => ({ ...prev, fuel_reference_consumption: e.target.value }))}
                            placeholder="e.g. 8.5" autoComplete="off" />
                          <p className="text-xs text-muted-foreground">Used by the Fuel page estimate engine</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Fuel Price ({assetForm.fuel_type === 'electric' ? '$/kWh' : '$/L'})</Label>
                          <Input type="number" min="0" step="0.001" data-testid="fleet-edit-fuel-price-input"
                            value={assetForm.fuel_price}
                            onChange={(e) => setAssetForm(prev => ({ ...prev, fuel_price: e.target.value }))}
                            placeholder="Global default" autoComplete="off" />
                          <p className="text-xs text-muted-foreground">Leave blank to use the global default price</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ── Manual Driver Allocation — vehicles only, needs Drivers/Staff privileges ── */}
                  {editingDevice.device_type !== 'beacon' && hasDriverAssignPriv && (
                    <div className="space-y-2 pt-3 border-t" data-testid="driver-assignment-section">
                      <Label className="flex items-center gap-1.5"><UserCog className="w-4 h-4 text-[#2563eb]" /> Driver Assignment</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={(assetForm.driver_assignment_mode || 'auto') === 'auto' ? 'default' : 'outline'}
                          onClick={() => setAssetForm(prev => ({ ...prev, driver_assignment_mode: 'auto' }))}
                          data-testid="driver-assignment-mode-auto"
                        >
                          Auto
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={assetForm.driver_assignment_mode === 'manual' ? 'default' : 'outline'}
                          onClick={() => setAssetForm(prev => ({ ...prev, driver_assignment_mode: 'manual' }))}
                          data-testid="driver-assignment-mode-manual"
                        >
                          Manual
                        </Button>
                      </div>
                      {assetForm.driver_assignment_mode === 'manual' && (
                        <Popover open={driverAssignPickerOpen} onOpenChange={setDriverAssignPickerOpen}>
                          <PopoverTrigger asChild>
                            <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="driver-assignment-person-select">
                              <span className="truncate">
                                {(() => {
                                  const sel = driverAssignOptions.find((p) => `${p.person_type}:${p.id}` === assetForm.manual_assigned_person);
                                  if (sel) return `${sel.name} — ${sel.person_type === 'driver' ? 'Driver' : 'Staff (eligible to drive)'}`;
                                  if (assetForm.manual_assigned_person && editingDevice.manual_assigned_person?.person_name) return editingDevice.manual_assigned_person.person_name;
                                  return 'Select a driver or eligible staff member';
                                })()}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 z-[10000] w-[--radix-popover-trigger-width]" align="start">
                            <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                              <CommandInput placeholder="Search drivers or staff..." data-testid="driver-assignment-person-search" />
                              <CommandList>
                                <CommandEmpty data-testid="driver-assignment-no-results">No available person found</CommandEmpty>
                                <CommandGroup>
                                  {driverAssignOptions.map((p) => {
                                    const val = `${p.person_type}:${p.id}`;
                                    return (
                                      <CommandItem
                                        key={val}
                                        value={`${p.name} ${p.person_type === 'driver' ? 'driver' : 'staff'} ${p.id}`}
                                        onSelect={() => {
                                          setAssetForm((prev) => ({ ...prev, manual_assigned_person: val }));
                                          setDriverAssignPickerOpen(false);
                                        }}
                                        data-testid={`driver-assignment-option-${p.id}`}
                                      >
                                        <Check className={`mr-2 h-4 w-4 shrink-0 ${assetForm.manual_assigned_person === val ? 'opacity-100' : 'opacity-0'}`} />
                                        <span className="truncate">{p.name}</span>
                                        <span className="ml-auto pl-2 text-[10px] text-muted-foreground shrink-0">{p.person_type === 'driver' ? 'Driver' : 'Staff'}</span>
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                      <p className="text-xs text-muted-foreground" data-testid="driver-assignment-hint">
                        {assetForm.driver_assignment_mode === 'manual'
                          ? 'Used when no driver is detected. A real RFID/BLE scan or login always overrides the manual assignment. Changes apply from the next ignition on.'
                          : 'Auto: the driver is detected from RFID/iButton scans and BLE beacons.'}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* GPS Trailer/Equipment — Odometer & Engine Hours parity with vehicles (admin-controlled sources) */}
              {!isVehicleType(editingDevice.asset_type) && !isContainerType(editingDevice.asset_type) && editingDevice.device_type !== 'beacon'
                && ((editingDevice.odometer_source && editingDevice.odometer_source !== 'off') || (editingDevice.engine_hours_source && editingDevice.engine_hours_source !== 'off')) && (
                <div className="grid grid-cols-2 gap-4">
                  {editingDevice.odometer_source && editingDevice.odometer_source !== 'off' && (
                  <div className="space-y-2">
                    <Label>Odometer (km)</Label>
                    <Input
                      type="number"
                      value={assetForm.odometer_reading}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, odometer_reading: e.target.value }))}
                      placeholder="0"
                      disabled={editingDevice.odometer_source !== 'gps'}
                      data-testid="fleet-edit-odometer-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {editingDevice.odometer_source === 'gps' && 'Editable — calculated from GPS data'}
                      {editingDevice.odometer_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                    </p>
                  </div>
                  )}
                  {editingDevice.engine_hours_source && editingDevice.engine_hours_source !== 'off' && (
                  <div className="space-y-2">
                    <Label>Engine Hours</Label>
                    <Input
                      type="number"
                      value={assetForm.engine_hours}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, engine_hours: e.target.value }))}
                      placeholder="0"
                      disabled={editingDevice.engine_hours_source !== 'ignition'}
                      data-testid="fleet-edit-engine-hours-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {editingDevice.engine_hours_source === 'ignition' && 'Editable — counted when ignition is ON'}
                      {editingDevice.engine_hours_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                    </p>
                  </div>
                  )}
                </div>
              )}

              {/* Trailer-specific fields */}
              {isTrailerType(editingDevice.asset_type) && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Make</Label>
                      <Input
                        value={assetForm.asset_make}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_make: e.target.value }))}
                        placeholder="Manufacturer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Input
                        value={assetForm.asset_model}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_model: e.target.value }))}
                        placeholder="Model"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Input
                        value={assetForm.asset_year}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, asset_year: e.target.value }))}
                        placeholder="2024"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Registration</Label>
                      <Input
                        value={assetForm.registration}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, registration: e.target.value }))}
                        placeholder="TRL-001"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>VIN</Label>
                      <Input
                        value={assetForm.vin_number}
                        onChange={(e) => setAssetForm(prev => ({ ...prev, vin_number: e.target.value }))}
                        placeholder="Vehicle Identification Number"
                        className="font-mono"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Container-specific fields */}
              {isContainerType(editingDevice.asset_type) && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Container Number</Label>
                    <Input
                      value={assetForm.container_number}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, container_number: e.target.value }))}
                      placeholder="CNTR-001"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Container Size</Label>
                    <Input
                      value={assetForm.container_size}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, container_size: e.target.value }))}
                      placeholder="20ft"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Container Type</Label>
                    <Input
                      value={assetForm.container_type}
                      onChange={(e) => setAssetForm(prev => ({ ...prev, container_type: e.target.value }))}
                      placeholder="Dry / Reefer"
                    />
                  </div>
                </div>
              )}

              {/* Generic equipment fields (tool, pallet, cargo, etc.) */}
              {!isVehicleType(editingDevice.asset_type) && !isTrailerType(editingDevice.asset_type) && !isContainerType(editingDevice.asset_type) && (
                <div className="space-y-2">
                  <Label>Serial Number</Label>
                  <Input
                    value={assetForm.serial_no}
                    onChange={(e) => setAssetForm(prev => ({ ...prev, serial_no: e.target.value }))}
                    placeholder="SN-12345"
                    className="font-mono"
                  />
                </div>
              )}
              {/* Home vehicle — asset beacons only (Left-Behind Asset event pairing) */}
              {editingDevice.device_type === 'beacon' && (
                <div className="space-y-2 p-3 rounded-lg border bg-muted/30" data-testid="home-vehicle-section">
                  <Label>Home Vehicle</Label>
                  <p className="text-[10px] text-muted-foreground">
                    The vehicle this asset is supposed to travel in. Used by the "Left-Behind Asset" event rule —
                    if the home vehicle drives off without this asset, an event fires.
                  </p>
                  <div className="flex gap-2">
                    <Select
                      value={assetForm.home_vehicle_id || 'none'}
                      onValueChange={(v) => setAssetForm(prev => ({ ...prev, home_vehicle_id: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger className="flex-1" data-testid="home-vehicle-select">
                        <SelectValue placeholder="No home vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No home vehicle</SelectItem>
                        {homeVehicleOptions.map(v => (
                          <SelectItem key={v.id} value={v.id} data-testid={`home-vehicle-option-${v.id}`}>
                            {v.asset_name || v.name || v.registration || v.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="shrink-0" onClick={suggestHomeVehicle} disabled={suggestingHome} data-testid="home-vehicle-suggest-btn">
                      {suggestingHome ? 'Checking…' : 'Suggest'}
                    </Button>
                  </div>
                </div>
              )}
              {/* Documents — all asset types (vehicles, trailers, equipment), matching the Staff Member modal layout */}
              <div className="border-t pt-3 mt-1 space-y-2" data-testid="vehicle-documents-block">
                <Label>Documents</Label>
                <VehicleAttachments
                  deviceId={editingDevice.id}
                  attachments={vehicleDocs}
                  onChange={(atts) => { setVehicleDocs(atts); refreshDocsSummary(); }}
                  hideLabel
                />
                <p className="text-xs text-muted-foreground">Registration, insurance and inspection documents with expiry dates — you'll get a warning notification before they expire (Alerts → Document Expiry rule).</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              onClick={handleSaveAsset}
              disabled={editSubmitting}
              data-testid="save-asset-btn"
            >
              {editSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <EmailReportModal open={emailModal.open} onOpenChange={(open) => setEmailModal((p) => ({ ...p, open }))} api={api} ctx={emailModal.ctx} />
      <DriverConfirmDialog
        api={api}
        candidateId={driverConfirm.candidateId}
        open={driverConfirm.open}
        onOpenChange={(open) => setDriverConfirm((p) => ({ ...p, open }))}
        onResolved={() => {
          setDriverConfirm({ open: false, candidateId: null });
          refreshVehicleDriverLogbookInPlace();
        }}
      />
      <ActiveDriverCorrectionDialog
        api={api}
        device={driverCorrectionDevice}
        open={!!driverCorrectionDevice}
        onOpenChange={(open) => { if (!open) setDriverCorrectionDevice(null); }}
        onResolved={() => { fetchFleetDevices(); applyVehicleDriverLogbookFilters(); }}
      />
    </div>
  );
};
